/**
 * Backup — kompletní správa záloh s plánováním, retention managementem, cloud sync.
 * 
 * Sekce:
 * 1. Stats Grid — počet záloh, plánovaných, velikost, cloud sync
 * 2. Plánované zálohy — TaskScheduler s filtrem backup
 * 3. Backup Scheduler — speciální komponenta pro backup scheduler
 * 4. Seznam záloh — tabulka + kartičky s detaily
 * 5. Retention management — zobrazení retention politik, manuální cleanup
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Select } from '../../components/ui/Select';
import { Input } from '../../components/ui/Input';
import { Modal } from '../../components/ui/Modal';
import { StatsGrid, StatCard } from '../../components/ui/Stats';
import { PageHeader } from '../../components/ui/PageHeader';
import { EmptyState } from '../../components/ui/EmptyState';
import { Tab, TabList, TabPanel, Tabs } from '../../components/ui';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { useIsPhoneLayout } from '../../lib/ui-layout';
import { callWidgetTool } from '../../widgets/scope';
import type { EffectiveScope } from '../../widgets/types';
import { ModuleScopeBar } from '../../widgets/ModuleScopeBar';
import {
  Archive,
  HardDrive,
  RefreshCw,
  Plus,
  Play,
  Trash2,
  Edit2,
  Cloud,
  Clock,
  CalendarClock,
  Settings,
  Table2,
  LayoutGrid,
  Shield,
  Scale,
  Zap,
  Minus,
  Check,
  Download,
  Search,
  Box,
  Filter,
  Link2,
} from 'lucide-react';
import { themedConfirm } from '../../lib/themed-dialogs';

/** Node-aware MCP — stejným způsobem jako toolbox widgety. */
let _backupScope: EffectiveScope = { node: null, identity: null };
function setBackupScope(scope: EffectiveScope) {
  _backupScope = scope;
}
function mcpCall<T = unknown>(name: string, args: Record<string, unknown> = {}) {
  return callWidgetTool(name, args, _backupScope) as Promise<T>;
}

// ── Types ───────────────────────────────────────────────────────────────────

interface BackupSchedule {
  id: string;
  name: string;
  description: string | null;
  target_type: string;
  target_name: string | null;
  backup_type: string;
  /** Rozsah zálohy modulu: secrets | essential | full. */
  backup_scope?: string;
  schedule_type: string;
  schedule_config: Record<string, unknown>;
  retention_count: number;
  retention_days: number;
  cloud_sync: string | null;
  enabled: boolean;
  last_run_at: string | null;
  last_run_status: string | null;
  last_run_id: string | null;
  next_run_at: string | null;
  created_at: string;
  updated_at: string;
  /** Identita záměru pro vrstvenou retenci (migrace 115). */
  project_id?: string | null;
  tenant_id?: string | null;
}

interface BackupMeta {
  id: string;
  name: string;
  container: string;
  image: string;
  volumes: string[];
  /** Kanonické pole. */
  created_at: string;
  /** Legacy alias — staré meta.json měly jen `created`. */
  created?: string;
  size_bytes: number;
  size?: string;
  status: string;
  metadata: Record<string, unknown>;
  retention_count?: number;
  retention_days?: number;
  target_type?: string;
  target_name?: string;
}

type ViewMode = 'table' | 'cards';

// ── Backup Scheduler Component ──────────────────────────────────────────────

const CRON_PRESETS = [
  { value: '0 2 * * *', labelKey: 'backup.cronDaily2am' },
  { value: '0 3 * * *', labelKey: 'backup.cronDaily3am' },
  { value: '0 */6 * * *', labelKey: 'backup.cronEvery6h' },
  { value: '0 * * * *', labelKey: 'backup.cronHourly' },
  { value: '0 3 * * 0', labelKey: 'backup.cronWeekly' },
  { value: '0 3 1 * *', labelKey: 'backup.cronMonthly' },
  { value: 'custom', labelKey: 'backup.cronCustom' },
];

/** Parse crontab / systemd OnCalendar-ish / "daily 2:00" into cron. */
function parseScheduleImport(raw: string): string | null {
  const s = raw.trim();
  if (!s) return null;
  // 5-field cron
  if (/^(\S+\s+){4}\S+$/.test(s) && !s.includes('=')) return s;
  // @daily / @hourly / @weekly / @monthly
  const at: Record<string, string> = {
    '@hourly': '0 * * * *',
    '@daily': '0 2 * * *',
    '@weekly': '0 3 * * 0',
    '@monthly': '0 3 1 * *',
    '@reboot': '0 2 * * *',
  };
  if (at[s.toLowerCase()]) return at[s.toLowerCase()];
  // daily at HH:MM
  const m = s.match(/^(?:daily|denně)\s*(?:at|v)?\s*(\d{1,2}):(\d{2})$/i);
  if (m) return `${Number(m[2])} ${Number(m[1])} * * *`;
  // every N hours
  const h = s.match(/^every\s+(\d+)\s*h(?:ours?)?$/i);
  if (h) return `0 */${h[1]} * * *`;
  // every N minutes
  const mi = s.match(/^every\s+(\d+)\s*m(?:in(?:utes?)?)?$/i);
  if (mi) return `*/${mi[1]} * * * *`;
  // systemd OnCalendar=daily
  if (/^OnCalendar=/i.test(s)) {
    const v = s.replace(/^OnCalendar=/i, '').trim().toLowerCase();
    if (v === 'daily') return '0 2 * * *';
    if (v === 'weekly') return '0 3 * * 0';
    if (v === 'hourly') return '0 * * * *';
  }
  return null;
}

function BackupSchedulerManager() {
  const { t } = useTranslation();
  const phone = useIsPhoneLayout();
  const qc = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState<BackupSchedule | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>('cards');
  const [filterTarget, setFilterTarget] = useState<string>('all');
  const [filterEnabled, setFilterEnabled] = useState<'all' | 'on' | 'off'>('all');
  const [schedSearch, setSchedSearch] = useState('');
  const effectiveViewMode: ViewMode = phone ? 'cards' : viewMode;

  // Fetch schedules — queryFn VŽDY vrací pole (stejný key jako RetentionManager;
  // dřív tu ležel celý MCP objekt → RetentionManager: S.filter is not a function).
  const { data: schedulesData, isLoading } = useQuery({
    queryKey: ['backup-schedules'],
    queryFn: async () => {
      const r = await mcpCall<{ schedules?: unknown }>('toolbox.backup_scheduler_list');
      return (Array.isArray(r?.schedules) ? r.schedules : []) as BackupSchedule[];
    },
    staleTime: 30_000,
  });

  const allSchedules = Array.isArray(schedulesData) ? schedulesData : [];
  const filteredSchedules = allSchedules.filter((s) => {
    if (filterTarget !== 'all' && s.target_type !== filterTarget) return false;
    if (filterEnabled === 'on' && !s.enabled) return false;
    if (filterEnabled === 'off' && s.enabled) return false;
    const q = schedSearch.trim().toLowerCase();
    if (q) {
      const hay = `${s.name} ${s.target_name ?? ''} ${s.description ?? ''} ${s.target_type}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });

  // Create schedule
  const createMut = useMutation({
    mutationFn: (opts: {
      name: string;
      target_type: 'module' | 'container' | 'project' | 'all';
      target_name?: string;
      schedule_type: 'cron' | 'interval';
      schedule_config: Record<string, unknown>;
      retention_count: number;
      retention_days: number;
      cloud_sync?: string;
      scope?: BackupScope;
    }) => mcpCall('toolbox.backup_scheduler_create', opts),
    onSuccess: () => {
      toast.success(t('backup.scheduleCreated'));
      setShowCreate(false);
      qc.invalidateQueries({ queryKey: ['backup-schedules'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  // Update schedule
  const updateMut = useMutation({
    mutationFn: (opts: { id: string } & Partial<BackupSchedule>) =>
      mcpCall('toolbox.backup_scheduler_update', opts),
    onSuccess: () => {
      toast.success(t('backup.scheduleUpdated'));
      setEditingSchedule(null);
      qc.invalidateQueries({ queryKey: ['backup-schedules'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  // Delete schedule
  const deleteMut = useMutation({
    mutationFn: (id: string) =>
      mcpCall('toolbox.backup_scheduler_delete', { id }),
    onSuccess: () => {
      toast.success(t('backup.scheduleDeleted'));
      qc.invalidateQueries({ queryKey: ['backup-schedules'] });
    },
  });

  // Run now
  const runMut = useMutation({
    mutationFn: (id: string) =>
      mcpCall('toolbox.backup_scheduler_run', { id }),
    onSuccess: () => toast.success(t('backup.backupStarted')),
  });

  return (
    <Card>
      <CardHeader className='flex-col sm:flex-row items-start sm:items-center gap-2'>
        <div className='flex items-center justify-between flex-wrap gap-2 w-full'>
          <CardTitle icon={CalendarClock}>
            {t('backup.schedulerTitle', { count: filteredSchedules.length })}
            {filteredSchedules.length !== allSchedules.length && (
              <span className='text-gray-600 font-normal text-xs ml-1'>/ {allSchedules.length}</span>
            )}
          </CardTitle>
          <div className='flex flex-wrap items-center gap-2'>
            {!phone && (
              <Button
                variant='secondary'
                size='sm'
                onClick={() => setViewMode(viewMode === 'table' ? 'cards' : 'table')}
                icon={viewMode === 'table' ? <LayoutGrid size={12} /> : <Table2 size={12} />}
              >
                {viewMode === 'table' ? t('backup.cardsView') : t('backup.tableView')}
              </Button>
            )}
            <Button
              variant='primary'
              size='sm'
              onClick={() => setShowCreate(true)}
              icon={<Plus size={12} />}
            >
              {t('backup.newSchedule')}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className='space-y-3'>
        {/* Filters */}
        <div className='flex flex-col sm:flex-row gap-2'>
          <div className='relative flex-1 min-w-0'>
            <Search size={13} className='absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-600' />
            <input
              className='w-full bg-gray-950 border border-gray-800 rounded-xl pl-8 pr-3 py-2 text-sm text-gray-200 placeholder-gray-600 focus:border-blue-500 focus:outline-none'
              placeholder={t('backup.schedSearchPh')}
              value={schedSearch}
              onChange={(e) => setSchedSearch(e.target.value)}
            />
          </div>
          <Select
            size='sm'
            className='sm:w-36'
            value={filterTarget}
            onChange={(e) => setFilterTarget(e.target.value)}
            options={[
              { value: 'all', label: t('backup.filterAll') },
              { value: 'module', label: t('backup.filterModules') },
              { value: 'project', label: t('backup.filterProjects') },
              { value: 'container', label: t('backup.filterContainers') },
            ]}
          />
          <Select
            size='sm'
            className='sm:w-32'
            value={filterEnabled}
            onChange={(e) => setFilterEnabled(e.target.value as 'all' | 'on' | 'off')}
            options={[
              { value: 'all', label: t('backup.filterAllStatus') },
              { value: 'on', label: t('backup.active') },
              { value: 'off', label: t('backup.inactive') },
            ]}
          />
        </div>

        {/* Create modal */}
        <Modal
          open={showCreate}
          onClose={() => setShowCreate(false)}
          title={t('backup.newScheduleTitle')}
          size='lg'
        >
          <CreateScheduleForm
            onSubmit={(opts) => createMut.mutate(opts)}
            isPending={createMut.isPending}
            onCancel={() => setShowCreate(false)}
          />
        </Modal>

        {/* Edit modal */}
        <Modal
          open={!!editingSchedule}
          onClose={() => setEditingSchedule(null)}
          title={t('backup.editTitle', { name: editingSchedule?.name })}
          size='lg'
        >
          {editingSchedule && (
            <EditScheduleForm
              schedule={editingSchedule}
              onSubmit={(opts) => updateMut.mutate({ id: editingSchedule.id, ...opts })}
              isPending={updateMut.isPending}
              onCancel={() => setEditingSchedule(null)}
            />
          )}
        </Modal>

        {/* Content */}
        {isLoading
          ? (
            <div className='flex justify-center py-4'>
              <RefreshCw size={14} className='animate-spin text-gray-600' />
            </div>
          )
          : filteredSchedules.length === 0
          ? <EmptyState icon={<CalendarClock size={24} />} title={t('backup.noSchedules')} description={t('backup.noSchedulesDesc')} />
          : effectiveViewMode === 'table'
          ? <ScheduleTable schedules={filteredSchedules} onEdit={setEditingSchedule} onDelete={deleteMut.mutate} onRun={runMut.mutate} />
          : <ScheduleCards schedules={filteredSchedules} onEdit={setEditingSchedule} onDelete={deleteMut.mutate} onRun={runMut.mutate} />}
      </CardContent>
    </Card>
  );
}

// ── Schedule Table ──────────────────────────────────────────────────────────

function ScheduleTable({ schedules, onEdit, onDelete, onRun }: {
  schedules: BackupSchedule[];
  onEdit: (s: BackupSchedule) => void;
  onDelete: (id: string) => void;
  onRun: (id: string) => void;
}) {
  const { t } = useTranslation();
  return (
    <div className='overflow-x-auto'>
      <table className='w-full text-sm'>
        <thead>
          <tr className='border-b border-gray-800 text-gray-500 text-xs'>
            <th className='text-left py-2 px-3'>{t('backup.colName')}</th>
            <th className='text-left py-2 px-3'>{t('backup.colTarget')}</th>
            <th className='text-left py-2 px-3'>{t('backup.colSchedule')}</th>
            <th className='text-left py-2 px-3'>{t('backup.colRetention')}</th>
            <th className='text-left py-2 px-3'>{t('backup.colCloud')}</th>
            <th className='text-left py-2 px-3'>{t('backup.colStatus')}</th>
            <th className='text-left py-2 px-3'>{t('backup.colNextRun')}</th>
            <th className='text-right py-2 px-3'>{t('backup.colActions')}</th>
          </tr>
        </thead>
        <tbody>
          {schedules.map((s) => (
            <tr key={s.id} className='border-b border-gray-800/50 hover:bg-gray-900/30'>
              <td className='py-2 px-3'>
                <div className='font-medium text-white'>{s.name}</div>
                {s.description && <div className='text-xs text-gray-600'>{s.description}</div>}
              </td>
              <td className='py-2 px-3'>
                <Badge variant='info'>{s.target_type}</Badge>
                {s.backup_scope && s.backup_scope !== 'full' && (
                  <Badge variant='default'>{t(`backup.scope_${s.backup_scope}`)}</Badge>
                )}
                {s.target_name && <span className='text-xs text-gray-400 ml-1'>{s.target_name}</span>}
              </td>
              <td className='py-2 px-3 font-mono text-xs'>
                {s.schedule_type === 'cron'
                  ? (s.schedule_config as { cron?: string })?.cron
                  : t('backup.everySeconds', { seconds: (s.schedule_config as { seconds?: number })?.seconds })}
              </td>
              <td className='py-2 px-3 text-xs'>
                {t('backup.retentionSummary', { count: s.retention_count, days: s.retention_days })}
              </td>
              <td className='py-2 px-3'>
                {s.cloud_sync
                  ? <Badge variant='success'><Cloud size={10} className='inline mr-1' />{s.cloud_sync}</Badge>
                  : <span className='text-gray-600'>—</span>}
              </td>
              <td className='py-2 px-3'>
                <Badge variant={s.enabled ? 'success' : 'error'}>{s.enabled ? t('backup.active') : t('backup.inactive')}</Badge>
                {s.last_run_status && (
                  <Badge
                    variant={s.last_run_status === 'ok'
                      ? 'success'
                      : s.last_run_status === 'running'
                      ? 'info'
                      : 'error'}
                    className='ml-1'
                  >
                    {s.last_run_status}
                  </Badge>
                )}
              </td>
              <td className='py-2 px-3 text-xs text-gray-400'>
                {s.next_run_at ? new Date(s.next_run_at).toLocaleString('cs') : '—'}
              </td>
              <td className='py-2 px-3 text-right'>
                <div className='flex items-center justify-end gap-1'>
                  <button
                    onClick={() => onRun(s.id)}
                    className='p-1 text-gray-600 hover:text-emerald-400 rounded'
                    title={t('backup.runNow')}
                  >
                    <Play size={12} />
                  </button>
                  <button
                    onClick={() => onEdit(s)}
                    className='p-1 text-gray-600 hover:text-yellow-400 rounded'
                    title={t('backup.edit')}
                  >
                    <Edit2 size={12} />
                  </button>
                  <button
                    onClick={async () => {
                      if (await themedConfirm(t('backup.deleteConfirm', { name: s.name }))) onDelete(s.id);
                    }}
                    className='p-1 text-gray-600 hover:text-red-400 rounded'
                    title={t('backup.delete')}
                  >
                    <Trash2 size={12} />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Schedule Cards ──────────────────────────────────────────────────────────

function scheduleExpr(s: BackupSchedule, everySeconds: string): string {
  if (s.schedule_type === 'cron') {
    return (s.schedule_config as { cron?: string })?.cron ?? '—';
  }
  return everySeconds;
}

function ScheduleCards({ schedules, onEdit, onDelete, onRun }: {
  schedules: BackupSchedule[];
  onEdit: (s: BackupSchedule) => void;
  onDelete: (id: string) => void;
  onRun: (id: string) => void;
}) {
  const { t } = useTranslation();
  return (
    <div className='grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3'>
      {schedules.map((s) => (
        <div
          key={s.id}
          className='rounded-2xl border border-gray-800 bg-gray-950/50 p-3.5 flex flex-col gap-3 hover:border-gray-700 transition-colors'
        >
          <div className='flex items-start gap-2.5 min-w-0'>
            <div className={`rounded-xl p-2 shrink-0 ${s.enabled ? 'bg-emerald-500/10 text-emerald-400' : 'bg-gray-800 text-gray-500'}`}>
              <CalendarClock size={16} />
            </div>
            <div className='min-w-0 flex-1'>
              <div className='flex items-center gap-2 flex-wrap'>
                <h4 className='font-semibold text-white text-sm truncate'>{s.name}</h4>
                <Badge variant={s.enabled ? 'success' : 'gray'}>
                  {s.enabled ? t('backup.active') : t('backup.inactive')}
                </Badge>
              </div>
              {s.description && (
                <p className='text-[11px] text-gray-500 mt-0.5 line-clamp-2'>{s.description}</p>
              )}
            </div>
          </div>

          <div className='grid grid-cols-2 gap-x-3 gap-y-1.5 text-[11px]'>
            <div className='text-gray-600'>{t('backup.colTarget')}</div>
            <div className='text-right min-w-0'>
              <Badge variant='info' className='text-[10px]'>{s.target_type}</Badge>
              {s.target_name && (
                <span className='block font-mono text-gray-400 truncate mt-0.5'>{s.target_name}</span>
              )}
            </div>
            <div className='text-gray-600'>{t('backup.colSchedule')}</div>
            <div
              className='text-right font-mono text-gray-300 truncate'
              title={scheduleExpr(
                s,
                t('backup.everySeconds', {
                  seconds: (s.schedule_config as { seconds?: number })?.seconds ?? 0,
                }),
              )}
            >
              {scheduleExpr(
                s,
                t('backup.everySeconds', {
                  seconds: (s.schedule_config as { seconds?: number })?.seconds ?? 0,
                }),
              )}
            </div>
            <div className='text-gray-600'>{t('backup.colRetention')}</div>
            <div className='text-right text-gray-400'>
              {t('backup.retentionSummary', { count: s.retention_count, days: s.retention_days })}
            </div>
            <div className='text-gray-600'>{t('backup.colNextRun')}</div>
            <div className='text-right text-gray-400'>
              {s.next_run_at ? new Date(s.next_run_at).toLocaleString() : '—'}
            </div>
            {s.cloud_sync && (
              <>
                <div className='text-gray-600'>{t('backup.colCloud')}</div>
                <div className='text-right'>
                  <Badge variant='success' className='text-[10px]'>
                    <Cloud size={10} className='inline mr-0.5' />{s.cloud_sync}
                  </Badge>
                </div>
              </>
            )}
          </div>

          {s.last_run_status && (
            <div className='text-[11px] text-gray-500 flex items-center gap-1.5'>
              <span>{t('backup.lastRun')}:</span>
              <Badge
                variant={s.last_run_status === 'ok'
                  ? 'success'
                  : s.last_run_status === 'running'
                  ? 'info'
                  : 'error'}
                className='text-[10px]'
              >
                {s.last_run_status}
              </Badge>
            </div>
          )}

          <div className='flex flex-wrap gap-1.5 pt-2 border-t border-gray-800/80 mt-auto'>
            <Button variant='secondary' size='sm' onClick={() => onRun(s.id)} icon={<Play size={12} />}>
              {t('backup.run')}
            </Button>
            <Button variant='ghost' size='sm' onClick={() => onEdit(s)} icon={<Edit2 size={12} />}>
              {t('backup.edit')}
            </Button>
            <Button
              variant='ghost'
              size='sm'
              className='ml-auto text-red-400/80 hover:text-red-300'
              onClick={async () => {
                if (await themedConfirm(t('backup.deleteConfirm', { name: s.name }))) onDelete(s.id);
              }}
              icon={<Trash2 size={12} />}
            >
              {t('backup.delete')}
            </Button>
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Rozsah zálohy modulu ────────────────────────────────────────────────────
// Filip 29. 10. 2026: „zvolit, jestli chceme jen secret store, nebo nějakou
// konkrétní označenou část, nebo celý kontejner."
type BackupScope = 'secrets' | 'essential' | 'full';

function BackupScopeSelect({ value, onChange }: { value: BackupScope; onChange: (v: BackupScope) => void }) {
  const { t } = useTranslation();
  const help: Record<BackupScope, string> = {
    secrets: t('backup.scopeHelpSecrets'),
    essential: t('backup.scopeHelpEssential'),
    full: t('backup.scopeHelpFull'),
  };
  return (
    <div className='space-y-1'>
      <label className='text-xs text-gray-500 block'>{t('backup.scopeLabel')}</label>
      <Select
        value={value}
        onChange={(e) => onChange(e.target.value as BackupScope)}
        options={[
          { value: 'secrets', label: t('backup.scopeSecrets') },
          { value: 'essential', label: t('backup.scopeEssential') },
          { value: 'full', label: t('backup.scopeFull') },
        ]}
      />
      <p className='text-[11px] text-gray-500'>{help[value]}</p>
    </div>
  );
}

// ── Create/Edit Forms ───────────────────────────────────────────────────────

function CreateScheduleForm({ onSubmit, isPending, onCancel }: {
  onSubmit: (opts: {
    name: string;
    target_type: 'module' | 'container' | 'project' | 'all';
    target_name?: string;
    schedule_type: 'cron' | 'interval';
    schedule_config: Record<string, unknown>;
    retention_count: number;
    retention_days: number;
    cloud_sync?: string;
    scope?: BackupScope;
  }) => void;
  isPending: boolean;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const [scope, setScope] = useState<BackupScope>('full');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [targetType, setTargetType] = useState<'module' | 'container' | 'project' | 'all'>('module');
  const [targetName, setTargetName] = useState('');
  const [targetSearch, setTargetSearch] = useState('');
  const [scheduleType, setScheduleType] = useState<'cron' | 'interval'>('cron');
  const [cronPreset, setCronPreset] = useState('0 2 * * *');
  const [cronExpr, setCronExpr] = useState('0 2 * * *');
  const [importRaw, setImportRaw] = useState('');
  const [intervalSec, setIntervalSec] = useState(3600);
  const [retentionCount, setRetentionCount] = useState(7);
  const [retentionDays, setRetentionDays] = useState(30);
  const [cloudSync, setCloudSync] = useState('');

  const targetsQuery = useQuery({
    queryKey: ['backup-targets'],
    queryFn: () =>
      mcpCall<{
        modules: Array<{ name: string; label: string; maturity?: string }>;
        containers: Array<{ name: string; module?: string }>;
        projects?: Array<{ project_id: string; name: string; label: string; container_count: number }>;
        groups: Record<string, string[]>;
      }>('toolbox.backup_targets_list'),
  });
  const targets = targetsQuery.data ?? { modules: [], containers: [], projects: [], groups: {} };

  const filteredModules = (Array.isArray(targets.modules) ? targets.modules : []).filter((m) => {
    const q = targetSearch.trim().toLowerCase();
    if (!q) return true;
    return `${m.name} ${m.label}`.toLowerCase().includes(q);
  });
  const filteredContainers = (Array.isArray(targets.containers) ? targets.containers : []).filter(
    (c) => {
      const q = targetSearch.trim().toLowerCase();
      if (!q) return true;
      return `${c.name} ${c.module ?? ''}`.toLowerCase().includes(q);
    },
  );
  const filteredProjects = (Array.isArray(targets.projects) ? targets.projects : []).filter((p) => {
    const q = targetSearch.trim().toLowerCase();
    if (!q) return true;
    return `${p.project_id} ${p.name} ${p.label}`.toLowerCase().includes(q);
  });

  const applyImport = () => {
    const parsed = parseScheduleImport(importRaw);
    if (!parsed) {
      toast.error(t('backup.importScheduleFail'));
      return;
    }
    setScheduleType('cron');
    setCronPreset('custom');
    setCronExpr(parsed);
    toast.success(t('backup.importScheduleOk', { cron: parsed }));
  };

  const handleSubmit = () => {
    if (!name) {
      toast.error(t('backup.nameRequired'));
      return;
    }
    if (targetType !== 'all' && !targetName) {
      toast.error(t('backup.targetNameRequired'));
      return;
    }
    const cron = cronPreset === 'custom' ? cronExpr : cronPreset;
    onSubmit({
      name,
      target_type: targetType,
      target_name: targetType !== 'all' ? targetName : undefined,
      schedule_type: scheduleType,
      schedule_config: scheduleType === 'cron' ? { cron } : { seconds: intervalSec },
      retention_count: retentionCount,
      retention_days: retentionDays,
      cloud_sync: cloudSync || undefined,
      ...(targetType === 'module' || targetType === 'all' ? { scope } : {}),
    });
  };

  return (
    <div className='space-y-4 max-h-[min(70vh,32rem)] overflow-y-auto pr-1'>
      <div>
        <label className='text-xs text-gray-500 mb-1 block'>{t('backup.colName')}</label>
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder={t('backup.phScheduleName')} />
      </div>
      <div>
        <label className='text-xs text-gray-500 mb-1 block'>{t('backup.descriptionOptional')}</label>
        <Input value={description} onChange={(e) => setDescription(e.target.value)} placeholder={t('backup.phDescription')} />
      </div>

      <div className='space-y-2'>
        <label className='text-xs text-gray-500 block'>{t('backup.colTarget')}</label>
        <div className='flex flex-wrap gap-1.5'>
          {([
            ['module', t('backup.targetModule')],
            ['project', t('backup.targetProject')],
            ['container', t('backup.targetContainer')],
            ['all', t('backup.targetAllModules')],
          ] as const).map(([val, lab]) => (
            <button
              key={val}
              type='button'
              onClick={() => {
                setTargetType(val);
                setTargetName('');
                setTargetSearch('');
              }}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium ${
                targetType === val ? 'bg-blue-600 text-white' : 'bg-gray-800 text-gray-400'
              }`}
            >
              {lab}
            </button>
          ))}
        </div>

        {targetType !== 'all' && (
          <>
            <div className='relative'>
              <Search size={12} className='absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-600' />
              <input
                className='w-full bg-gray-950 border border-gray-800 rounded-lg pl-8 pr-2 py-1.5 text-xs text-gray-200 placeholder-gray-600 focus:border-blue-500 focus:outline-none'
                placeholder={targetType === 'project' ? t('backup.searchProject') : t('backup.searchTarget')}
                value={targetSearch}
                onChange={(e) => setTargetSearch(e.target.value)}
              />
            </div>
            <div className='max-h-40 overflow-auto rounded-xl border border-gray-800 divide-y divide-gray-800/60'>
              {targetsQuery.isLoading && (
                <div className='p-3 text-xs text-gray-500'>{t('backup.loadingShort')}</div>
              )}
              {targetType === 'module' && filteredModules.map((m) => (
                <button
                  key={m.name}
                  type='button'
                  onClick={() => setTargetName(m.name)}
                  className={`w-full text-left px-3 py-2 text-xs hover:bg-gray-900/60 ${
                    targetName === m.name ? 'bg-blue-500/15 text-blue-300' : 'text-gray-300'
                  }`}
                >
                  <span className='font-medium'>{m.label || m.name}</span>
                  {m.label !== m.name && (
                    <span className='text-gray-600 font-mono ml-1.5'>{m.name}</span>
                  )}
                </button>
              ))}
              {targetType === 'project' && filteredProjects.map((p) => (
                <button
                  key={p.project_id}
                  type='button'
                  onClick={() => setTargetName(p.project_id)}
                  className={`w-full text-left px-3 py-2 text-xs hover:bg-gray-900/60 ${
                    targetName === p.project_id ? 'bg-blue-500/15 text-blue-300' : 'text-gray-300'
                  }`}
                >
                  <div className='font-medium'>{p.name}</div>
                  <div className='text-[10px] text-gray-600 font-mono mt-0.5'>
                    {p.project_id}
                    {p.container_count > 0 ? ` · ${p.container_count} ctr` : ''}
                  </div>
                </button>
              ))}
              {targetType === 'container' && filteredContainers.map((c) => (
                <button
                  key={c.name}
                  type='button'
                  onClick={() => setTargetName(c.name)}
                  className={`w-full text-left px-3 py-2 text-xs hover:bg-gray-900/60 ${
                    targetName === c.name ? 'bg-blue-500/15 text-blue-300' : 'text-gray-300'
                  }`}
                >
                  <span className='font-mono'>{c.name}</span>
                  {c.module && <span className='text-gray-600 ml-1.5'>({c.module})</span>}
                </button>
              ))}
              {targetType === 'module' && !targetsQuery.isLoading && filteredModules.length === 0 && (
                <div className='p-3 text-xs text-gray-600'>{t('backup.noTargetsMatch')}</div>
              )}
              {targetType === 'project' && !targetsQuery.isLoading && filteredProjects.length === 0 && (
                <div className='p-3 text-xs text-gray-600'>{t('backup.noTargetsMatch')}</div>
              )}
              {targetType === 'container' && !targetsQuery.isLoading && filteredContainers.length === 0 && (
                <div className='p-3 text-xs text-gray-600'>{t('backup.noTargetsMatch')}</div>
              )}
            </div>
            {targetName && (
              <div className='text-[11px] text-emerald-400/90 font-mono'>
                {t('backup.selectedTarget')}: {targetName}
              </div>
            )}
          </>
        )}
      </div>

      <div className='space-y-2'>
        <label className='text-xs text-gray-500 block'>{t('backup.colSchedule')}</label>
        <div className='flex gap-1.5'>
          {(['cron', 'interval'] as const).map((st) => (
            <button
              key={st}
              type='button'
              onClick={() => setScheduleType(st)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium ${
                scheduleType === st ? 'bg-blue-600 text-white' : 'bg-gray-800 text-gray-400'
              }`}
            >
              {st === 'cron' ? 'Cron' : t('backup.interval')}
            </button>
          ))}
        </div>
        {scheduleType === 'cron'
          ? (
            <div className='space-y-2'>
              <Select
                size='sm'
                value={cronPreset}
                onChange={(e) => {
                  setCronPreset(e.target.value);
                  if (e.target.value !== 'custom') setCronExpr(e.target.value);
                }}
                options={CRON_PRESETS.map((p) => ({
                  value: p.value,
                  label: t(p.labelKey),
                }))}
              />
              {(cronPreset === 'custom' || !CRON_PRESETS.some((p) => p.value === cronPreset)) && (
                <Input
                  value={cronExpr}
                  onChange={(e) => setCronExpr(e.target.value)}
                  placeholder='0 2 * * *'
                  className='font-mono'
                />
              )}
              <div className='rounded-xl border border-dashed border-gray-800 p-2.5 space-y-1.5'>
                <div className='text-[10px] text-gray-500'>{t('backup.importScheduleHint')}</div>
                <div className='flex gap-2'>
                  <Input
                    value={importRaw}
                    onChange={(e) => setImportRaw(e.target.value)}
                    placeholder='@daily · every 6 hours · 0 3 * * * · OnCalendar=daily'
                    className='font-mono text-xs flex-1'
                  />
                  <Button variant='secondary' size='sm' onClick={applyImport}>
                    {t('backup.importSchedule')}
                  </Button>
                </div>
              </div>
            </div>
          )
          : (
            <div className='flex items-center gap-2'>
              <span className='text-xs text-gray-500'>{t('backup.every')}</span>
              <Input
                type='number'
                value={intervalSec}
                onChange={(e) => setIntervalSec(Number(e.target.value))}
                className='w-28 font-mono'
              />
              <span className='text-xs text-gray-500'>{t('backup.seconds')}</span>
            </div>
          )}
      </div>

      <div className='grid grid-cols-1 sm:grid-cols-2 gap-3'>
        <div>
          <label className='text-xs text-gray-500 mb-1 block'>{t('backup.phRetentionCount')}</label>
          <Input type='number' value={retentionCount} onChange={(e) => setRetentionCount(Number(e.target.value))} />
        </div>
        <div>
          <label className='text-xs text-gray-500 mb-1 block'>{t('backup.phDays')}</label>
          <Input type='number' value={retentionDays} onChange={(e) => setRetentionDays(Number(e.target.value))} />
        </div>
      </div>

      {(targetType === 'module' || targetType === 'all') && (
        <BackupScopeSelect value={scope} onChange={setScope} />
      )}

      <Input value={cloudSync} onChange={(e) => setCloudSync(e.target.value)} placeholder={t('backup.phCloudSyncOptional')} />

      <div className='flex gap-2 pt-2 border-t border-gray-800 sticky bottom-0 bg-gray-950/90'>
        <Button variant='secondary' size='sm' onClick={onCancel}>{t('common.cancel')}</Button>
        <Button variant='primary' size='sm' onClick={handleSubmit} disabled={isPending}>{t('backup.create')}</Button>
      </div>
    </div>
  );
}

function ManualBackupPanel() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [kind, setKind] = useState<'module' | 'container' | 'project'>('module');
  const [scope, setScope] = useState<BackupScope>('full');
  const [target, setTarget] = useState('');
  const [search, setSearch] = useState('');
  const [cloudSync, setCloudSync] = useState('');

  const targetsQuery = useQuery({
    queryKey: ['backup-targets'],
    queryFn: () =>
      mcpCall<{
        modules: Array<{ name: string; label: string }>;
        containers: Array<{ name: string; module?: string; project_id?: string }>;
        projects?: Array<{
          project_id: string;
          name: string;
          label: string;
          container_count: number;
          env_names?: string[];
        }>;
      }>('toolbox.backup_targets_list'),
  });
  const modules = (Array.isArray(targetsQuery.data?.modules) ? targetsQuery.data.modules : [])
    .filter((m) => {
      const q = search.trim().toLowerCase();
      return !q || `${m.name} ${m.label}`.toLowerCase().includes(q);
    });
  const containers =
    (Array.isArray(targetsQuery.data?.containers) ? targetsQuery.data.containers : []).filter(
      (c) => {
        const q = search.trim().toLowerCase();
        return !q ||
          `${c.name} ${c.module ?? ''} ${c.project_id ?? ''}`.toLowerCase().includes(q);
      },
    );
  const projects = (Array.isArray(targetsQuery.data?.projects) ? targetsQuery.data.projects : [])
    .filter((p) => {
      const q = search.trim().toLowerCase();
      return !q ||
        `${p.project_id} ${p.name} ${p.label} ${(Array.isArray(p.env_names) ? p.env_names : []).join(' ')}`
          .toLowerCase().includes(q);
    });

  const runMut = useMutation({
    mutationFn: async () => {
      if (!target) throw new Error(t('backup.targetNameRequired'));
      if (kind === 'module') {
        return mcpCall<{ ok?: boolean; error?: string; name?: string; size?: string }>(
          'toolbox.backup_module',
          { module: target, cloud_sync: cloudSync || undefined, scope },
        );
      }
      if (kind === 'project') {
        // Kompletní kontejnery projektu (label acm.project)
        return mcpCall<{
          ok?: boolean;
          error?: string;
          name?: string;
          results?: Array<{ name?: string }>;
          empty?: boolean;
          message?: string;
        }>('toolbox.backup_group_create', {
          project_id: target,
          label: `project-${target}`,
        });
      }
      return mcpCall<{ ok?: boolean; error?: string; name?: string }>(
        'toolbox.backup_create',
        { container: target },
      );
    },
    onSuccess: (r) => {
      if (r && 'error' in r && r.error) {
        toast.error(r.error);
        return;
      }
      if (r && 'empty' in r && r.empty) {
        toast.error(r.message ?? t('backup.projectEmpty'));
        return;
      }
      const name = (r as { name?: string })?.name ||
        (r as { results?: Array<{ name?: string }> })?.results?.[0]?.name ||
        target;
      toast.success(
        t('backup.manualOk', {
          name,
          size: (r as { size?: string })?.size ?? '',
        }),
      );
      qc.invalidateQueries({ queryKey: ['backups'] });
      qc.invalidateQueries({ queryKey: ['toolbox-backups'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const listItems: Array<{ id: string; label: string; sub?: string }> =
    kind === 'module'
      ? modules.map((m) => ({
        id: m.name,
        label: m.label !== m.name ? `${m.label} (${m.name})` : m.name,
      }))
      : kind === 'project'
      ? projects.map((p) => ({
        id: p.project_id,
        label: p.label || `${p.name} (${p.project_id})`,
        sub: p.container_count > 0
          ? t('backup.projectCtrCount', { count: p.container_count })
          : t('backup.projectNoLiveCtr'),
      }))
      : containers.map((c) => ({
        id: c.name,
        label: c.module ? `${c.name} (${c.module})` : c.name,
        sub: c.project_id,
      }));

  return (
    <Card>
      <CardHeader>
        <CardTitle icon={Archive}>{t('backup.manualBackupTitle')}</CardTitle>
      </CardHeader>
      <CardContent className='space-y-3'>
        <p className='text-xs text-gray-500'>{t('backup.manualBackupHint')}</p>
        <div className='flex flex-wrap gap-1.5'>
          {([
            ['module', t('backup.targetModule')],
            ['project', t('backup.targetProject')],
            ['container', t('backup.targetContainer')],
          ] as const).map(([val, lab]) => (
            <button
              key={val}
              type='button'
              onClick={() => { setKind(val); setTarget(''); setSearch(''); }}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium ${
                kind === val ? 'bg-blue-600 text-white' : 'bg-gray-800 text-gray-400'
              }`}
            >
              {lab}
            </button>
          ))}
        </div>
        {kind === 'project' && (
          <p className='text-[11px] text-gray-600'>{t('backup.projectBackupHint')}</p>
        )}
        <div className='relative'>
          <Search size={12} className='absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-600' />
          <input
            className='w-full bg-gray-950 border border-gray-800 rounded-lg pl-8 pr-2 py-1.5 text-xs text-gray-200 placeholder-gray-600 focus:border-blue-500 focus:outline-none'
            placeholder={kind === 'project' ? t('backup.searchProject') : t('backup.searchTarget')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className='max-h-40 overflow-auto rounded-xl border border-gray-800 divide-y divide-gray-800/50'>
          {listItems.map((item) => (
            <button
              key={item.id}
              type='button'
              onClick={() => setTarget(item.id)}
              className={`w-full text-left px-3 py-2 text-xs ${
                target === item.id ? 'bg-blue-500/15 text-blue-300' : 'text-gray-300 hover:bg-gray-900/50'
              }`}
            >
              <div className='font-medium break-all'>{item.label}</div>
              {item.sub && <div className='text-[10px] text-gray-600 mt-0.5'>{item.sub}</div>}
            </button>
          ))}
          {!targetsQuery.isLoading && listItems.length === 0 && (
            <div className='p-3 text-xs text-gray-600'>{t('backup.noTargetsMatch')}</div>
          )}
        </div>
        {kind === 'module' && <BackupScopeSelect value={scope} onChange={setScope} />}
        {kind !== 'project' && (
          <Input
            value={cloudSync}
            onChange={(e) => setCloudSync(e.target.value)}
            placeholder={t('backup.phCloudSyncOptional')}
          />
        )}
        <Button
          variant='primary'
          size='sm'
          disabled={!target || runMut.isPending}
          loading={runMut.isPending}
          onClick={() => runMut.mutate()}
          icon={<Archive size={12} />}
        >
          {kind === 'project' ? t('backup.runProjectBackup') : t('backup.runManualNow')}
          {target ? `: ${target}` : ''}
        </Button>
      </CardContent>
    </Card>
  );
}

function EditScheduleForm({ schedule, onSubmit, isPending, onCancel }: {
  schedule: BackupSchedule;
  onSubmit: (opts: Partial<BackupSchedule>) => void;
  isPending: boolean;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const [name, setName] = useState(schedule.name);
  const [targetType, setTargetType] = useState(schedule.target_type as 'module' | 'container' | 'all');
  const [targetName, setTargetName] = useState(schedule.target_name ?? '');

  const targetsQuery = useQuery({
    queryKey: ['backup-targets'],
    queryFn: () =>
      mcpCall<{
        modules: Array<{ name: string; label: string; maturity?: string }>;
        containers: Array<{ name: string; module?: string }>;
        groups: Record<string, string[]>;
      }>('toolbox.backup_targets_list'),
  });
  const targets = targetsQuery.data ?? { modules: [], containers: [], groups: {} };
  const [scheduleType, setScheduleType] = useState(schedule.schedule_type as 'cron' | 'interval');
  const [cronExpr, setCronExpr] = useState((schedule.schedule_config as { cron?: string })?.cron ?? '');
  const [intervalSec, setIntervalSec] = useState((schedule.schedule_config as { seconds?: number })?.seconds ?? 3600);
  const [retentionCount, setRetentionCount] = useState(schedule.retention_count);
  const [retentionDays, setRetentionDays] = useState(schedule.retention_days);
  const [cloudSync, setCloudSync] = useState(schedule.cloud_sync ?? '');
  const [scope, setScope] = useState<BackupScope>(
    (['secrets', 'essential', 'full'] as const).find((v) => v === schedule.backup_scope) ?? 'full',
  );

  const handleSubmit = () => {
    onSubmit({
      name,
      target_type: targetType,
      target_name: targetType !== 'all' ? targetName || null : null,
      schedule_type: scheduleType,
      schedule_config: scheduleType === 'cron' ? { cron: cronExpr } : { seconds: intervalSec },
      retention_count: retentionCount,
      retention_days: retentionDays,
      cloud_sync: cloudSync || null,
      ...(targetType === 'module' || targetType === 'all' ? { scope } : {}),
    } as Partial<BackupSchedule>);
  };

  return (
    <div className='space-y-3'>
      <Input value={name} onChange={(e) => setName(e.target.value)} placeholder={t('backup.phName')} />

      <div className='grid grid-cols-1 sm:grid-cols-2 gap-3'>
        <Select
          value={targetType}
          onChange={(e) => setTargetType(e.target.value as 'module' | 'container' | 'all')}
          options={[
            { value: 'module', label: t('backup.targetModule') },
            { value: 'container', label: t('backup.targetContainer') },
            { value: 'all', label: t('backup.targetAllModules') },
          ]}
        />
        {targetType === 'module' && (
          <Select
            value={targetName}
            onChange={(e) => setTargetName(e.target.value)}
            options={[
              { value: '', label: targetsQuery.isLoading ? t('backup.loadingShort') : t('backup.selectModule') },
              ...((targets.modules ?? []) as Array<{ name: string; label: string }>)
                .map((m) => ({
                  value: m.name,
                  label: m.label !== m.name ? `${m.label} (${m.name})` : m.name,
                })),
            ]}
          />
        )}
        {targetType === 'container' && (
          <Select
            value={targetName}
            onChange={(e) => setTargetName(e.target.value)}
            options={[
              { value: '', label: targetsQuery.isLoading ? t('backup.loadingShort') : t('backup.selectContainer') },
              ...((targets.containers ?? []) as Array<{ name: string; module?: string }>)
                .map((c) => ({
                  value: c.name,
                  label: c.module ? t('backup.containerWithModule', { name: c.name, module: c.module }) : c.name,
                })),
            ]}
          />
        )}
      </div>

      <div className='grid grid-cols-1 sm:grid-cols-2 gap-3'>
        <div className='flex gap-1.5'>
          {(['cron', 'interval'] as const).map((st) => (
            <button
              key={st}
              onClick={() => setScheduleType(st)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium ${
                scheduleType === st ? 'bg-blue-600 text-white' : 'bg-gray-800 text-gray-400'
              }`}
            >
              {st === 'cron' ? 'Cron' : 'Interval'}
            </button>
          ))}
        </div>
        {scheduleType === 'cron'
          ? <Input value={cronExpr} onChange={(e) => setCronExpr(e.target.value)} placeholder='0 2 * * *' className='font-mono' />
          : <Input type='number' value={intervalSec} onChange={(e) => setIntervalSec(Number(e.target.value))} placeholder='3600' className='font-mono' />}
      </div>

      <div className='grid grid-cols-1 sm:grid-cols-2 gap-3'>
        <Input type='number' value={retentionCount} onChange={(e) => setRetentionCount(Number(e.target.value))} placeholder={t('backup.phRetentionCount')} />
        <Input type='number' value={retentionDays} onChange={(e) => setRetentionDays(Number(e.target.value))} placeholder={t('backup.phDays')} />
      </div>

      {(targetType === 'module' || targetType === 'all') && (
        <BackupScopeSelect value={scope} onChange={setScope} />
      )}

      <Input value={cloudSync} onChange={(e) => setCloudSync(e.target.value)} placeholder='Cloud sync ID' />

      <div className='flex gap-2 pt-2 border-t border-gray-800'>
        <Button variant='secondary' size='sm' onClick={onCancel}>{t('common.cancel')}</Button>
        <Button variant='primary' size='sm' onClick={handleSubmit} disabled={isPending}>{t('common.save')}</Button>
      </div>
    </div>
  );
}

// ── Retention Manager ───────────────────────────────────────────────────────

// ── Standard Retention Policies ─────────────────────────────────────────────

const STANDARD_POLICIES = [
  {
    id: 'conservative',
    name: 'backup.policyConservative',
    description: 'backup.policyConservativeDesc',
    retention_count: 14,
    retention_days: 60,
    icon: Shield,
    color: 'text-green-400',
  },
  {
    id: 'balanced',
    name: 'backup.policyBalanced',
    description: 'backup.policyBalancedDesc',
    retention_count: 7,
    retention_days: 30,
    icon: Scale,
    color: 'text-blue-400',
  },
  {
    id: 'aggressive',
    name: 'backup.policyAggressive',
    description: 'backup.policyAggressiveDesc',
    retention_count: 3,
    retention_days: 7,
    icon: Zap,
    color: 'text-orange-400',
  },
  {
    id: 'minimal',
    name: 'backup.policyMinimal',
    description: 'backup.policyMinimalDesc',
    retention_count: 2,
    retention_days: 14,
    icon: Minus,
    color: 'text-gray-400',
  },
] as const;

type RetentionScopeMode = 'all' | 'tenant' | 'project';

interface RetentionLimitLayer {
  value: number;
  set_by: string | null;
  set_at: string | null;
}

interface RetentionLimitResolved {
  key: string;
  platform: { default: number; max: number };
  module: RetentionLimitLayer | null;
  tenant: RetentionLimitLayer | null;
  project: RetentionLimitLayer | null;
  user: RetentionLimitLayer | null;
  resolved: number;
  source: string;
}

// ── Retention Stats Card ────────────────────────────────────────────────────

function RetentionStatsCard() {
  const { t } = useTranslation();
  const { data: backupsData } = useQuery({
    queryKey: ['backups'],
    queryFn: async () => {
      const result = await mcpCall<{ backups?: BackupMeta[] }>('toolbox.backup_list');
      return Array.isArray(result.backups) ? result.backups : [];
    },
    staleTime: 30_000,
  });

  const backups: BackupMeta[] = Array.isArray(backupsData) ? (backupsData as BackupMeta[]) : [];
  const now = Date.now();
  const dayMs = 86400000;

  const stats = {
    total: backups.length,
    olderThan30d: backups.filter((b) => (now - new Date(b.created_at).getTime()) > 30 * dayMs).length,
    olderThan7d: backups.filter((b) => (now - new Date(b.created_at).getTime()) > 7 * dayMs).length,
    today: backups.filter((b) => {
      const d = new Date(b.created_at);
      const today = new Date();
      return d.toDateString() === today.toDateString();
    }).length,
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle icon={Clock}>{t('backup.ageOverview')}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className='grid grid-cols-2 md:grid-cols-4 gap-2'>
          <div className='bg-gray-900 rounded-lg p-3 border border-gray-800'>
            <div className='text-xs text-gray-500 mb-1'>{t('backup.total')}</div>
            <div className='text-2xl font-bold text-white'>{stats.total}</div>
          </div>
          <div className='bg-gray-900 rounded-lg p-3 border border-gray-800'>
            <div className='text-xs text-gray-500 mb-1'>{t('backup.today')}</div>
            <div className='text-2xl font-bold text-green-400'>{stats.today}</div>
          </div>
          <div className='bg-gray-900 rounded-lg p-3 border border-gray-800'>
            <div className='text-xs text-gray-500 mb-1'>{t('backup.olderThan7')}</div>
            <div className='text-2xl font-bold text-orange-400'>{stats.olderThan7d}</div>
          </div>
          <div className='bg-gray-900 rounded-lg p-3 border border-gray-800'>
            <div className='text-xs text-gray-500 mb-1'>{t('backup.olderThan30')}</div>
            <div className='text-2xl font-bold text-red-400'>{stats.olderThan30d}</div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ── Retention Manager ───────────────────────────────────────────────────────

function RetentionManager() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [scopeMode, setScopeMode] = useState<RetentionScopeMode>('all');
  const [scopeId, setScopeId] = useState('');
  const [ceilingDays, setCeilingDays] = useState('');
  const [activePolicy, setActivePolicy] = useState<string | null>(null);
  const [applying, setApplying] = useState(false);
  // Který režim právě běží — kvůli spinneru na správném tlačítku.
  const [retentionForce, setRetentionForce] = useState(false);
  const [lastResult, setLastResult] = useState<string | null>(null);

  const targetsQuery = useQuery({
    queryKey: ['backup-targets'],
    queryFn: () =>
      mcpCall<{
        projects?: Array<{
          project_id: string;
          name: string;
          label: string;
          container_count: number;
          tenant_id?: string | null;
        }>;
        tenants?: Array<{ id: string; slug: string; name: string }>;
      }>('toolbox.backup_targets_list'),
    staleTime: 60_000,
  });
  const tenants = Array.isArray(targetsQuery.data?.tenants) ? targetsQuery.data.tenants : [];
  const projects = Array.isArray(targetsQuery.data?.projects) ? targetsQuery.data.projects : [];

  const schedulesQuery = useQuery({
    queryKey: ['backup-schedules'],
    queryFn: async () => {
      const r = await mcpCall<{ schedules?: unknown }>('toolbox.backup_scheduler_list');
      return (Array.isArray(r?.schedules) ? r.schedules : []) as BackupSchedule[];
    },
    staleTime: 30_000,
  });
  const schedules = Array.isArray(schedulesQuery.data) ? schedulesQuery.data : [];

  const limitQueryArgs =
    scopeMode === 'tenant' && scopeId
      ? { key: 'backup.retention_days', tenant_id: scopeId }
      : scopeMode === 'project' && scopeId
      ? {
        key: 'backup.retention_days',
        project_id: scopeId,
        tenant_id: projects.find((p) => p.project_id === scopeId)?.tenant_id ?? undefined,
      }
      : { key: 'backup.retention_days' };

  const limitQuery = useQuery({
    queryKey: ['backup-retention-limit', scopeMode, scopeId],
    queryFn: async () => {
      const r = await mcpCall<{
        ok?: boolean;
        limits?: RetentionLimitResolved[];
      }>('platform.limits_get', limitQueryArgs);
      return r.limits?.[0] ?? null;
    },
    staleTime: 15_000,
  });
  const limit = limitQuery.data;

  const matchSchedule = (s: BackupSchedule): boolean => {
    if (scopeMode === 'all') return true;
    if (!scopeId) return false;
    if (scopeMode === 'tenant') {
      if (s.tenant_id === scopeId) return true;
      // schedule s project_id, jehož tenant je vybraný
      if (s.project_id) {
        const p = projects.find((x) => x.project_id === s.project_id);
        if (p?.tenant_id === scopeId) return true;
      }
      if (s.target_type === 'project' && s.target_name) {
        const p = projects.find((x) => x.project_id === s.target_name);
        if (p?.tenant_id === scopeId) return true;
      }
      return false;
    }
    // project
    return s.project_id === scopeId ||
      (s.target_type === 'project' && s.target_name === scopeId);
  };

  const scopedSchedules = schedules.filter(matchSchedule);
  const scopeReady = scopeMode === 'all' || !!scopeId;

  // Sync ceiling input when limit loads
  const layerValue =
    scopeMode === 'tenant' ? limit?.tenant?.value
      : scopeMode === 'project' ? limit?.project?.value
      : null;

  const retentionMut = useMutation({
    mutationFn: (force: boolean) =>
      mcpCall<{
        ok?: boolean;
        message?: string;
        scheduled_pruned?: string[];
        unscheduled_pruned?: string[];
        freed_bytes?: number;
        errors?: unknown;
      }>('toolbox.backup_scheduler_retention', { dry_run: !force, force }),
    onSuccess: (data) => {
      const scheduled = data.scheduled_pruned ?? [];
      const unscheduled = data.unscheduled_pruned ?? [];
      const total = scheduled.length + unscheduled.length;
      const mb = data.freed_bytes != null ? Math.round(data.freed_bytes / 1048576) : null;
      const msg = data.message ??
        (retentionForce
          ? t('backup.retentionDeleted', { count: total })
          : t('backup.retentionDryRun', { count: total, mb: mb ?? '?' }));
      setLastResult(
        [
          msg,
          scheduled.length ? `scheduled: ${scheduled.slice(0, 5).join(', ')}${scheduled.length > 5 ? '…' : ''}` : '',
          unscheduled.length
            ? `unscheduled: ${unscheduled.slice(0, 5).join(', ')}${unscheduled.length > 5 ? '…' : ''}`
            : '',
        ].filter(Boolean).join('\n'),
      );
      if (data.ok === false) toast.error(msg, { duration: 8000 });
      else toast.success(msg, { duration: 6000 });
      qc.invalidateQueries({ queryKey: ['backups'] });
    },
    onError: (e: Error) => {
      setLastResult(e.message);
      toast.error(e.message);
    },
  });

  // ── Koš: pojistky pořízené před smazáním ────────────────────────────────
  // Pre-delete zálohy jsou z běžné retence vyňaté (`type != 'trash'`) a řídí se
  // vlastní retencí koše. Mechanismus existoval, ale nešel nastavit odjinud než
  // z MCP — v UI nebyl vůbec.
  const trashQ = useQuery({
    queryKey: ['trash-retention'],
    queryFn: () =>
      mcpCall<{
        retention_days?: number;
        default_days?: number;
        bins?: Record<string, { days?: number; inherited?: boolean; governs?: string }>;
      }>('toolbox.trash_retention', { action: 'get' }),
  });
  const trashListQ = useQuery({
    queryKey: ['trash-list'],
    queryFn: () =>
      mcpCall<{
        items?: Array<
          { group_label?: string; resource_name?: string; total_bytes?: number; retention_until?: string }
        >;
        total?: number;
      }>('toolbox.trash_list', {}),
  });
  const [trashDays, setTrashDays] = useState<number | null>(null);
  const trashMut = useMutation({
    mutationFn: (opts: { days?: number; unset?: boolean }) =>
      mcpCall('toolbox.trash_retention', {
        action: opts.unset ? 'unset' : 'set',
        bin: 'containers',
        ...(opts.days != null ? { days: opts.days } : {}),
      }),
    onSuccess: () => {
      toast.success(t('backup.trashRetentionSaved'));
      setTrashDays(null);
      qc.invalidateQueries({ queryKey: ['trash-retention'] });
      qc.invalidateQueries({ queryKey: ['trash-list'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const setCeilingMut = useMutation({
    mutationFn: async (opts: { value?: number; clear?: boolean }) => {
      if (scopeMode === 'all' || !scopeId) throw new Error(t('backup.retentionScopeRequired'));
      const layer = scopeMode === 'tenant' ? 'tenant' : 'project';
      if (opts.clear) {
        return mcpCall('platform.limits_set', {
          key: 'backup.retention_days',
          layer,
          scope_id: scopeId,
          clear: true,
        });
      }
      const value = Number(opts.value);
      if (!Number.isFinite(value) || value < 1) throw new Error(t('backup.retentionCeilingInvalid'));
      return mcpCall('platform.limits_set', {
        key: 'backup.retention_days',
        layer,
        scope_id: scopeId,
        value,
        note: `backup retention ceiling (${layer})`,
      });
    },
    onSuccess: (_, vars) => {
      toast.success(vars.clear ? t('backup.retentionCeilingCleared') : t('backup.retentionCeilingSaved'));
      qc.invalidateQueries({ queryKey: ['backup-retention-limit'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const handleApplyPolicy = async (policy: (typeof STANDARD_POLICIES)[number]) => {
    if (!scopeReady) {
      toast.error(t('backup.retentionScopeRequired'));
      return;
    }
    const scopeLabel =
      scopeMode === 'all'
        ? t('backup.retentionScopeAll')
        : scopeMode === 'tenant'
        ? (tenants.find((x) => x.id === scopeId)?.name ?? scopeId)
        : (projects.find((x) => x.project_id === scopeId)?.name ?? scopeId);

    const ok = await themedConfirm(
      t('backup.applyPolicyScopeConfirm', {
        name: t(policy.name),
        scope: scopeLabel,
        count: scopedSchedules.length,
        days: policy.retention_days,
        pieces: policy.retention_count,
      }),
    );
    if (!ok) return;

    setApplying(true);
    setActivePolicy(policy.id);
    try {
      let updated = 0;
      const errors: string[] = [];
      for (const s of scopedSchedules) {
        try {
          const r = await mcpCall<{ ok?: boolean; error?: string }>('toolbox.backup_scheduler_update', {
            id: s.id,
            retention_count: policy.retention_count,
            retention_days: policy.retention_days,
          });
          if (r.ok === false) errors.push(`${s.name}: ${r.error ?? 'fail'}`);
          else updated++;
        } catch (e) {
          errors.push(`${s.name}: ${(e as Error).message}`);
        }
      }

      // Strop limitu pro tenant/project — schedule nesmí překročit
      if (scopeMode === 'tenant' || scopeMode === 'project') {
        await mcpCall('platform.limits_set', {
          key: 'backup.retention_days',
          layer: scopeMode,
          scope_id: scopeId,
          value: policy.retention_days,
          note: `policy ${policy.id}`,
        });
      }

      const msg = t('backup.policyAppliedScope', {
        name: t(policy.name),
        updated,
        total: scopedSchedules.length,
      });
      setLastResult(
        [msg, errors.length ? errors.slice(0, 8).join('\n') : ''].filter(Boolean).join('\n'),
      );
      if (errors.length) toast.error(msg, { duration: 8000 });
      else toast.success(msg, { duration: 6000 });
      qc.invalidateQueries({ queryKey: ['backup-schedules'] });
      qc.invalidateQueries({ queryKey: ['backup-retention-limit'] });
    } catch (e) {
      toast.error((e as Error).message);
      setLastResult((e as Error).message);
    } finally {
      setApplying(false);
    }
  };

  return (
    <div className='space-y-4'>
      <RetentionStatsCard />

      {/* Scope: all / tenant / project */}
      <Card>
        <CardHeader>
          <CardTitle icon={Filter}>{t('backup.retentionScopeTitle')}</CardTitle>
        </CardHeader>
        <CardContent className='space-y-3'>
          <p className='text-xs text-gray-500'>{t('backup.retentionScopeHelp')}</p>
          <div className='flex flex-wrap gap-1.5'>
            {([
              ['all', t('backup.retentionScopeAll')],
              ['tenant', t('backup.retentionScopeTenant')],
              ['project', t('backup.retentionScopeProject')],
            ] as const).map(([val, lab]) => (
              <button
                key={val}
                type='button'
                onClick={() => {
                  setScopeMode(val);
                  setScopeId('');
                  setCeilingDays('');
                  setActivePolicy(null);
                }}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium ${
                  scopeMode === val ? 'bg-blue-600 text-white' : 'bg-gray-800 text-gray-400'
                }`}
              >
                {lab}
              </button>
            ))}
          </div>

          {scopeMode === 'tenant' && (
            <Select
              value={scopeId}
              onChange={(e) => {
                setScopeId(e.target.value);
                setCeilingDays('');
              }}
              options={[
                {
                  value: '',
                  label: targetsQuery.isLoading
                    ? t('backup.loadingShort')
                    : t('backup.retentionSelectTenant'),
                },
                ...tenants.map((tn) => ({
                  value: tn.id,
                  label: tn.slug ? `${tn.name} (${tn.slug})` : tn.name,
                })),
              ]}
            />
          )}

          {scopeMode === 'project' && (
            <Select
              value={scopeId}
              onChange={(e) => {
                setScopeId(e.target.value);
                setCeilingDays('');
              }}
              options={[
                {
                  value: '',
                  label: targetsQuery.isLoading
                    ? t('backup.loadingShort')
                    : t('backup.retentionSelectProject'),
                },
                ...projects.map((p) => ({
                  value: p.project_id,
                  label: `${p.name} (${p.project_id})`,
                })),
              ]}
            />
          )}

          {scopeReady && (
            <div className='text-xs text-gray-400 flex flex-wrap gap-3'>
              <span>
                {t('backup.retentionMatchingSchedules', { count: scopedSchedules.length })}
              </span>
              {limit && (
                <span>
                  {t('backup.retentionResolved', {
                    days: limit.resolved,
                    source: limit.source,
                  })}
                  {limit.platform?.max != null && (
                    <span className='text-gray-600'>
                      {' '}· {t('backup.retentionPlatformMax', { days: limit.platform.max })}
                    </span>
                  )}
                </span>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Ceiling for tenant / project */}
      {(scopeMode === 'tenant' || scopeMode === 'project') && scopeId && (
        <Card>
          <CardHeader>
            <CardTitle icon={Shield}>{t('backup.retentionCeilingTitle')}</CardTitle>
          </CardHeader>
          <CardContent className='space-y-3'>
            <p className='text-xs text-gray-500'>{t('backup.retentionCeilingHelp')}</p>
            <div className='flex flex-wrap items-end gap-2'>
              <div className='min-w-[8rem]'>
                <label className='text-[10px] text-gray-500 mb-1 block'>
                  {t('backup.retentionCeilingDays')}
                </label>
                <Input
                  type='number'
                  min={1}
                  value={ceilingDays !== '' ? ceilingDays : (layerValue != null ? String(layerValue) : '')}
                  onChange={(e) => setCeilingDays(e.target.value)}
                  placeholder={limit ? String(limit.resolved) : '30'}
                  className='font-mono'
                />
              </div>
              <Button
                variant='primary'
                size='sm'
                loading={setCeilingMut.isPending}
                disabled={setCeilingMut.isPending}
                onClick={() => {
                  const raw = ceilingDays !== '' ? ceilingDays : (layerValue != null ? String(layerValue) : '');
                  setCeilingMut.mutate({ value: Number(raw) });
                }}
              >
                {t('backup.retentionCeilingSave')}
              </Button>
              {layerValue != null && (
                <Button
                  variant='ghost'
                  size='sm'
                  loading={setCeilingMut.isPending}
                  disabled={setCeilingMut.isPending}
                  onClick={() => setCeilingMut.mutate({ clear: true })}
                >
                  {t('backup.retentionCeilingClear')}
                </Button>
              )}
            </div>
            {layerValue != null && (
              <div className='text-xs text-emerald-400/90'>
                {t('backup.retentionCeilingActive', { days: layerValue })}
              </div>
            )}
            {layerValue == null && (
              <div className='text-xs text-gray-600'>
                {t('backup.retentionCeilingInherited')}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Standard Policies */}
      <Card>
        <CardHeader>
          <CardTitle icon={Settings}>{t('backup.standardPolicies')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className='space-y-3'>
            <p className='text-xs text-gray-500'>
              {scopeMode === 'all'
                ? t('backup.policiesHelp')
                : t('backup.policiesHelpScoped')}
            </p>
            <div className='grid grid-cols-1 md:grid-cols-2 gap-3'>
              {STANDARD_POLICIES.map((policy) => {
                const IconComponent = policy.icon;
                return (
                  <div
                    key={policy.id}
                    className={`border rounded-lg p-3 transition-all ${
                      !scopeReady || applying
                        ? 'opacity-50 cursor-not-allowed border-gray-800'
                        : activePolicy === policy.id
                        ? 'border-blue-500 bg-blue-500/10 cursor-pointer'
                        : 'border-gray-800 hover:border-gray-700 cursor-pointer'
                    }`}
                    onClick={() => {
                      if (!scopeReady || applying) return;
                      void handleApplyPolicy(policy);
                    }}
                  >
                    <div className='flex items-start justify-between mb-2'>
                      <div className='flex items-center gap-2'>
                        <IconComponent size={18} className={policy.color} />
                        <h4 className='font-medium text-white text-sm'>{t(policy.name)}</h4>
                      </div>
                      <Badge variant={policy.id === 'balanced' ? 'success' : 'info'}>
                        {policy.id === 'balanced' ? t('backup.recommended') : t('backup.standard')}
                      </Badge>
                    </div>
                    <p className='text-xs text-gray-400 mb-2'>{t(policy.description)}</p>
                    <div className='flex gap-2 text-xs'>
                      <Badge variant='secondary'>{t('backup.piecesCount', { count: policy.retention_count })}</Badge>
                      <Badge variant='secondary'>{t('backup.daysCount', { count: policy.retention_days })}</Badge>
                    </div>
                  </div>
                );
              })}
            </div>

            {scopeReady && scopedSchedules.length > 0 && (
              <div className='rounded-xl border border-gray-800 divide-y divide-gray-800/60 max-h-40 overflow-auto'>
                {scopedSchedules.slice(0, 20).map((s) => (
                  <div
                    key={s.id}
                    className='px-3 py-1.5 text-[11px] flex items-center justify-between gap-2 text-gray-400'
                  >
                    <span className='truncate text-gray-300'>{s.name}</span>
                    <span className='shrink-0 font-mono text-gray-500'>
                      {t('backup.retentionSummary', {
                        count: s.retention_count,
                        days: s.retention_days,
                      })}
                    </span>
                  </div>
                ))}
                {scopedSchedules.length > 20 && (
                  <div className='px-3 py-1.5 text-[10px] text-gray-600'>
                    +{scopedSchedules.length - 20}
                  </div>
                )}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Koš — pojistky před smazáním mají vlastní retenci */}
      <Card>
        <CardHeader>
          <CardTitle icon={Trash2}>{t('backup.trashTitle')}</CardTitle>
        </CardHeader>
        <CardContent>
          <p className='text-xs text-gray-500'>{t('backup.trashHint')}</p>
          <div className='flex items-end gap-3 flex-wrap mt-3'>
            <label className='text-[11px] text-gray-400'>
              {t('backup.trashRetentionDays')}
              <input
                type='number'
                min={1}
                max={3650}
                value={trashDays ?? trashQ.data?.bins?.containers?.days ?? 30}
                onChange={(e) => setTrashDays(Number(e.target.value))}
                className='block w-24 mt-1 bg-gray-950 border border-gray-700 rounded-lg px-2 py-1 text-xs text-white'
              />
            </label>
            <Button
              size='sm'
              variant='secondary'
              loading={trashMut.isPending}
              disabled={trashMut.isPending || trashDays == null ||
                trashDays === trashQ.data?.bins?.containers?.days}
              onClick={() => trashMut.mutate({ days: trashDays ?? undefined })}
            >
              {t('backup.trashSave')}
            </Button>
            {trashQ.data?.bins?.containers?.inherited === false && (
              <Button
                size='sm'
                variant='ghost'
                disabled={trashMut.isPending}
                onClick={() => trashMut.mutate({ unset: true })}
              >
                {t('backup.trashInherit', { days: trashQ.data?.default_days ?? 30 })}
              </Button>
            )}
          </div>
          <p className='text-[10px] text-gray-600 mt-2'>{t('backup.trashReapNote')}</p>

          {(trashListQ.data?.items?.length ?? 0) > 0 && (
            <div className='mt-3 border border-gray-800 rounded-xl divide-y divide-gray-800'>
              {(trashListQ.data?.items ?? []).slice(0, 10).map((it) => (
                <div
                  key={it.group_label}
                  className='px-3 py-1.5 flex items-center gap-3 text-[11px]'
                >
                  <span className='text-gray-300 truncate'>{it.resource_name ?? it.group_label}</span>
                  <span className='ml-auto text-gray-500 shrink-0'>
                    {Math.round((it.total_bytes ?? 0) / 1048576)} MB
                  </span>
                  <span className='text-gray-600 shrink-0'>
                    {it.retention_until ? new Date(it.retention_until).toLocaleDateString() : '—'}
                  </span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Manual Actions */}
      <Card>
        <CardHeader>
          <CardTitle icon={Settings}>{t('backup.manualActions')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className='space-y-3'>
            {/* Dřív tu byl přepínač „dry run" ovládající dvě tlačítka, která
                navíc dělala totéž (prune byl zastaralý alias retence) — nešlo
                poznat, které z nich maže. Teď jsou to dvě akce: náhled a úklid. */}
            <p className='text-xs text-gray-500'>{t('backup.retentionActionsHint')}</p>
            <div className='flex flex-wrap gap-2'>
              <Button
                variant='secondary'
                size='sm'
                loading={retentionMut.isPending && !retentionForce}
                onClick={() => {
                  setRetentionForce(false);
                  retentionMut.mutate(false);
                }}
                disabled={retentionMut.isPending}
                icon={<Search size={12} />}
              >
                {t('backup.retentionPreview')}
              </Button>
              <Button
                variant='danger'
                size='sm'
                loading={retentionMut.isPending && retentionForce}
                onClick={async () => {
                  if (!(await themedConfirm(t('backup.retentionConfirm')))) return;
                  setRetentionForce(true);
                  retentionMut.mutate(true);
                }}
                disabled={retentionMut.isPending}
                icon={<Trash2 size={12} />}
              >
                {t('backup.retentionApply')}
              </Button>
            </div>
            {lastResult && (
              <pre className='text-[11px] text-gray-400 bg-gray-950/60 border border-gray-800 rounded-xl p-3 whitespace-pre-wrap max-h-40 overflow-auto'>
                {lastResult}
              </pre>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ── Main Backup Page ────────────────────────────────────────────────────────

function backupCreatedAt(b: BackupMeta): string {
  return b.created_at || b.created || '';
}

function backupAgeDays(b: BackupMeta): number | null {
  const created = backupCreatedAt(b);
  if (!created) return null;
  const t = new Date(created).getTime();
  if (isNaN(t)) return null;
  return Math.floor((Date.now() - t) / 86400000);
}

function ageTone(days: number | null): string {
  if (days === null) return 'text-gray-500';
  if (days > 30) return 'text-red-400';
  if (days > 7) return 'text-amber-400';
  return 'text-emerald-400';
}

interface ReplicaDestination {
  id: string;
  node_uuid: string;
  host?: string;
  label: string;
  auth: 'sso' | 'node_token';
  online?: boolean;
  source: 'mesh' | 'connection';
}

interface ReplicaContractRow {
  id: string;
  source_node: string;
  dest_node: string;
  project_ids: string[];
  purposes: string[];
  status: 'proposed' | 'active' | 'revoked';
  auth: string;
}

interface SyncVersionRow {
  id: string;
  contract_id: string;
  project_id: string;
  parent_id?: string;
  created_at: string;
  dest_node?: string;
  artifacts: string[];
  status: 'packed' | 'synced' | 'failed';
  error?: string;
}

function ReplicaContractsPanel() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [destUuid, setDestUuid] = useState('');
  const [projects, setProjects] = useState('');
  const [tokenId, setTokenId] = useState('');
  const [tokenHost, setTokenHost] = useState('');
  const [tokenUuid, setTokenUuid] = useState('');
  const [token, setToken] = useState('');
  const [syncProject, setSyncProject] = useState('');
  const [syncContract, setSyncContract] = useState('');
  const [pullSource, setPullSource] = useState('');

  const destQ = useQuery({
    queryKey: ['backup-destinations'],
    queryFn: () =>
      mcpCall<{ destinations?: ReplicaDestination[] }>('backup.destinations_list'),
    staleTime: 15_000,
  });
  const ctrQ = useQuery({
    queryKey: ['backup-contracts'],
    queryFn: () =>
      mcpCall<{ contracts?: ReplicaContractRow[]; self?: string }>('backup.contract_list'),
    staleTime: 10_000,
  });
  const destinations = destQ.data?.destinations ?? [];
  const contracts = ctrQ.data?.contracts ?? [];
  const self = ctrQ.data?.self ?? '';

  const proposeMut = useMutation({
    mutationFn: () =>
      mcpCall<{ ok?: boolean; error?: string; dest_notified?: boolean }>(
        'backup.contract_propose',
        {
          dest_node: destUuid,
          project_ids: projects.split(/[,\s]+/).map((s) => s.trim()).filter(Boolean),
        },
      ),
    onSuccess: (r) => {
      if (r && r.ok === false) {
        toast.error(r.error ?? t('backup.contractFailed', 'Kontrakt se nepodařilo navrhnout'));
        return;
      }
      toast.success(
        r.dest_notified
          ? t('backup.contractProposed', 'Kontrakt navržen, čeká na accept cíle')
          : t('backup.contractProposedLocal', 'Kontrakt uložen tu. Accept musí proběhnout na cíli.'),
      );
      void qc.invalidateQueries({ queryKey: ['backup-contracts'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const acceptMut = useMutation({
    mutationFn: (id: string) => mcpCall('backup.contract_accept', { id }),
    onSuccess: () => {
      toast.success(t('backup.contractAccepted', 'Kontrakt podepsán'));
      void qc.invalidateQueries({ queryKey: ['backup-contracts'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });
  const revokeMut = useMutation({
    mutationFn: (id: string) => mcpCall('backup.contract_revoke', { id }),
    onSuccess: () => {
      toast.success(t('backup.contractRevoked', 'Kontrakt zrušen'));
      void qc.invalidateQueries({ queryKey: ['backup-contracts'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });
  const destMut = useMutation({
    mutationFn: () =>
      mcpCall('backup.destination_set', {
        id: tokenId.trim(),
        node_uuid: tokenUuid.trim(),
        host: tokenHost.trim() || undefined,
        token: token.trim() || undefined,
      }),
    onSuccess: () => {
      toast.success(t('backup.destinationSaved', 'Cíl uložen'));
      setToken('');
      void qc.invalidateQueries({ queryKey: ['backup-destinations'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const versionsQ = useQuery({
    queryKey: ['backup-sync-versions', syncContract, syncProject],
    queryFn: () =>
      mcpCall<{ versions?: SyncVersionRow[] }>('backup.sync_versions', {
        ...(syncContract ? { contract_id: syncContract } : {}),
        ...(syncProject.trim() ? { project_id: syncProject.trim() } : {}),
      }),
    staleTime: 10_000,
  });
  const versions = versionsQ.data?.versions ?? [];

  const syncMut = useMutation({
    mutationFn: () =>
      mcpCall<{ ok?: boolean; error?: string; version?: SyncVersionRow }>(
        'backup.sync_push',
        { contract_id: syncContract, project_id: syncProject.trim() },
      ),
    onSuccess: (r) => {
      if (r && r.ok === false) {
        toast.error(r.error ?? t('backup.syncFailed', 'Sync selhal'));
        return;
      }
      toast.success(t('backup.syncOk', 'Backend verze je na cíli'));
      void qc.invalidateQueries({ queryKey: ['backup-sync-versions'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const remoteQ = useQuery({
    queryKey: ['backup-remote-projects', pullSource],
    queryFn: () =>
      mcpCall<{
        ok?: boolean;
        error?: string;
        projects?: Array<{
          project_id: string;
          name: string;
          slug?: string | null;
          status?: string | null;
        }>;
      }>('backup.remote_projects', { source_node: pullSource }),
    enabled: !!pullSource,
    staleTime: 15_000,
  });
  const remoteProjects = remoteQ.data?.projects ?? [];

  const migrateHereMut = useMutation({
    mutationFn: (project_id: string) =>
      mcpCall<{ ok?: boolean; error?: string }>('backup.migrate_here', {
        source_node: pullSource,
        project_id,
      }),
    onSuccess: (r) => {
      if (r && r.ok === false) {
        toast.error(r.error ?? t('backup.migrateHereFailed', 'Přesun selhal'));
        return;
      }
      toast.success(t('backup.migrateHereOk', 'Projekt se sem přesouvá'));
      void qc.invalidateQueries({ queryKey: ['backup-remote-projects'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const restoreMut = useMutation({
    mutationFn: (version_id: string) =>
      mcpCall<{ ok?: boolean; error?: string }>('backup.sync_restore', { version_id }),
    onSuccess: (r) => {
      if (r && r.ok === false) {
        toast.error(r.error ?? t('backup.syncRestoreFailed', 'Restore selhal'));
        return;
      }
      toast.success(t('backup.syncRestored', 'Backend obnoven z verze'));
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className='flex items-center gap-2 text-base'>
          <Link2 size={16} />
          {t('backup.replicaTitle', 'Cíle ACM — záloha a přesun')}
        </CardTitle>
      </CardHeader>
      <CardContent className='space-y-4'>
        <p className='text-xs text-gray-500'>
          {t(
            'backup.replicaHint',
            'Přesunout sem: vybereš uzel, kde máš práva. On projekt zastaví, zazálohuje, sem to stáhneme a obnovíme. Frontend z GitLabu, backend verzovaný backup.',
          )}
        </p>

        <div className='flex flex-wrap gap-2'>
          {destinations.length === 0 && (
            <span className='text-xs text-gray-500'>
              {t('backup.noDestinations', 'Žádný mesh peer ani propojený ACM.')}
            </span>
          )}
          {destinations.map((d) => (
            <button
              key={d.id}
              type='button'
              onClick={() => setDestUuid(d.node_uuid)}
              className={`rounded-full border px-3 py-1 text-xs ${
                destUuid === d.node_uuid
                  ? 'border-blue-500 bg-blue-500/15 text-blue-300'
                  : 'border-gray-800 text-gray-400'
              }`}
            >
              {d.label}
              {d.auth === 'node_token' ? ' · token' : ' · SSO'}
            </button>
          ))}
        </div>

        <div className='rounded-lg border border-gray-800 p-3 space-y-2'>
          <div className='text-sm font-medium text-gray-200'>
            {t('backup.pullHereTitle', 'Přesunout sem stávající projekt')}
          </div>
          <div className='flex flex-wrap gap-2'>
            {destinations.map((d) => (
              <button
                key={`pull-${d.id}`}
                type='button'
                onClick={() => setPullSource(d.node_uuid)}
                className={`rounded-full border px-3 py-1 text-xs ${
                  pullSource === d.node_uuid
                    ? 'border-emerald-500 bg-emerald-500/15 text-emerald-300'
                    : 'border-gray-800 text-gray-400'
                }`}
              >
                {d.label}
              </button>
            ))}
          </div>
          {pullSource && remoteQ.isLoading && (
            <div className='text-xs text-gray-500'>{t('common.loading')}</div>
          )}
          {remoteQ.data?.ok === false && (
            <div className='text-xs text-red-300'>{remoteQ.data.error}</div>
          )}
          {remoteProjects.map((p) => (
            <div key={p.project_id} className='flex items-center justify-between gap-2 text-sm'>
              <span className='text-gray-200 truncate'>
                {p.slug || p.name}
                <span className='text-[11px] text-gray-500 font-mono ml-2'>
                  {p.project_id.slice(0, 8)}
                </span>
              </span>
              <Button
                size='sm'
                variant='primary'
                loading={migrateHereMut.isPending && migrateHereMut.variables === p.project_id}
                disabled={migrateHereMut.isPending}
                onClick={async () => {
                  if (
                    await themedConfirm(
                      t('backup.migrateHereConfirm', {
                        name: p.slug || p.name,
                      }),
                    )
                  ) {
                    migrateHereMut.mutate(p.project_id);
                  }
                }}
              >
                {t('backup.migrateHere', 'Přesunout sem')}
              </Button>
            </div>
          ))}
          {pullSource && !remoteQ.isLoading && remoteProjects.length === 0 && !remoteQ.data?.error && (
            <div className='text-xs text-gray-500'>
              {t('backup.noRemoteProjects', 'Na zdroji žádný projekt, nebo na něj nemáš právo.')}
            </div>
          )}
        </div>

        <div className='grid grid-cols-1 sm:grid-cols-2 gap-2'>
          <Input
            value={destUuid}
            onChange={(e) => setDestUuid(e.target.value)}
            placeholder={t('backup.destUuidPh', 'UUID cílového uzlu')}
          />
          <Input
            value={projects}
            onChange={(e) => setProjects(e.target.value)}
            placeholder={t('backup.projectIdsPh', 'project id (volitelně, čárkou)')}
          />
        </div>
        <Button
          variant='primary'
          size='sm'
          disabled={!destUuid || proposeMut.isPending}
          loading={proposeMut.isPending}
          onClick={() => proposeMut.mutate()}
        >
          {t('backup.proposeContract', 'Navrhnout kontrakt')}
        </Button>

        <details className='text-xs text-gray-400'>
          <summary className='cursor-pointer'>
            {t('backup.addTokenDest', 'Přidat ACM přes node token')}
          </summary>
          <div className='mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2'>
            <Input value={tokenId} onChange={(e) => setTokenId(e.target.value)} placeholder='id (acm-dr-1)' />
            <Input value={tokenUuid} onChange={(e) => setTokenUuid(e.target.value)} placeholder='node UUID' />
            <Input value={tokenHost} onChange={(e) => setTokenHost(e.target.value)} placeholder='https://…' />
            <Input
              type='password'
              value={token}
              onChange={(e) => setToken(e.target.value)}
              placeholder='node token'
            />
          </div>
          <Button
            className='mt-2'
            variant='secondary'
            size='sm'
            disabled={!tokenId || !tokenUuid || destMut.isPending}
            loading={destMut.isPending}
            onClick={() => destMut.mutate()}
          >
            {t('backup.saveDestination', 'Uložit cíl')}
          </Button>
        </details>

        <div className='space-y-2'>
          {contracts.length === 0 && (
            <div className='text-xs text-gray-600'>
              {t('backup.noContracts', 'Žádný kontrakt.')}
            </div>
          )}
          {contracts.map((c) => (
            <div
              key={c.id}
              className='flex flex-col sm:flex-row sm:items-center justify-between gap-2 rounded-lg border border-gray-800 p-3'
            >
              <div className='min-w-0'>
                <div className='text-sm text-gray-200 font-mono truncate'>{c.id}</div>
                <div className='text-[11px] text-gray-500'>
                  {c.source_node.slice(0, 8)} → {c.dest_node.slice(0, 8)} · {c.status} · {c.purposes.join(', ')}
                </div>
              </div>
              <div className='flex gap-2'>
                {c.status === 'proposed' && c.dest_node.toLowerCase() === self.toLowerCase() && (
                  <Button size='sm' variant='primary' onClick={() => acceptMut.mutate(c.id)}>
                    {t('backup.accept', 'Accept')}
                  </Button>
                )}
                {c.status !== 'revoked' && (
                  <Button size='sm' variant='secondary' onClick={() => revokeMut.mutate(c.id)}>
                    {t('backup.revoke', 'Revoke')}
                  </Button>
                )}
                {c.status === 'active' && (
                  <Button
                    size='sm'
                    variant='secondary'
                    onClick={() => {
                      setSyncContract(c.id);
                      if (c.project_ids[0]) setSyncProject(c.project_ids[0]);
                    }}
                  >
                    {t('backup.useForSync', 'Sync backend')}
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>

        {syncContract && (
          <div className='space-y-2 rounded-lg border border-gray-800 p-3'>
            <div className='text-xs text-gray-400 font-mono truncate'>{syncContract}</div>
            <div className='flex flex-col sm:flex-row gap-2'>
              <Input
                value={syncProject}
                onChange={(e) => setSyncProject(e.target.value)}
                placeholder={t('backup.syncProjectPh', 'UUID projektu')}
              />
              <Button
                variant='primary'
                size='sm'
                disabled={!syncProject.trim() || syncMut.isPending}
                loading={syncMut.isPending}
                onClick={() => syncMut.mutate()}
              >
                {t('backup.pushBackend', 'Odeslat backend verzi')}
              </Button>
            </div>
            <div className='space-y-1'>
              {versions.map((v) => (
                <div key={v.id} className='flex items-center justify-between gap-2 text-xs'>
                  <span className='text-gray-400 truncate'>
                    {v.created_at.slice(0, 19).replace('T', ' ')} · {v.status}
                    {v.parent_id ? ' · child' : ''}
                    {v.artifacts.length ? ` · ${v.artifacts.length} artefaktů` : ''}
                  </span>
                  {v.status === 'synced' && v.dest_node && self &&
                    v.dest_node.toLowerCase() === self.toLowerCase() && (
                    <Button
                      size='sm'
                      variant='secondary'
                      onClick={() => restoreMut.mutate(v.id)}
                      loading={restoreMut.isPending}
                    >
                      {t('backup.restoreVersion', 'Obnovit')}
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function backupTargetLabel(b: BackupMeta): string {
  return b.target_name || b.container || '—';
}

export default function BackupPage() {
  const { t } = useTranslation();
  const phone = useIsPhoneLayout();
  const qc = useQueryClient();
  const [node, setNode] = useState<string | null>(null);
  const [selectedBackups, setSelectedBackups] = useState<Set<string>>(new Set());
  const [listView, setListView] = useState<'list' | 'cards'>('cards');
  const [search, setSearch] = useState('');
  const [filterTarget, setFilterTarget] = useState('all');
  const [sortBy, setSortBy] = useState<'date' | 'size' | 'name'>('date');
  // Telefon: vždy karty (tabulka je nepoužitelná)
  const effectiveListView = phone ? 'cards' : listView;

  setBackupScope({ node, identity: null });
  const scopeKey = node ?? 'local';

  const { data: backupsData, isLoading: loadingBackups, isFetching, refetch } = useQuery({
    queryKey: ['backups', scopeKey],
    queryFn: async () => {
      const result = await mcpCall<{ backups?: BackupMeta[] }>('toolbox.backup_list');
      const list = Array.isArray(result.backups) ? result.backups : [];
      return (list as BackupMeta[]).map((b) => ({
        ...b,
        id: b.id ?? b.name,
        created_at: b.created_at ?? b.created ?? '',
      }));
    },
    staleTime: 30_000,
  });

  const { data: schedulesData } = useQuery({
    queryKey: ['backup-schedules', scopeKey],
    queryFn: async () => {
      const r = await mcpCall<{ schedules?: unknown }>('toolbox.backup_scheduler_list');
      return (Array.isArray(r?.schedules) ? r.schedules : []) as BackupSchedule[];
    },
    staleTime: 30_000,
  });
  const scheduleCount = Array.isArray(schedulesData) ? schedulesData.length : 0;

  const reconcileMut = useMutation({
    mutationFn: () =>
      mcpCall<{ counts?: { new: number; updated: number; total: number } }>(
        'toolbox.backup_reconcile_db',
      ),
    onSuccess: (data: { counts?: { new: number; updated: number; total: number } }) => {
      const c = data?.counts ?? { new: 0, updated: 0, total: 0 };
      toast.success(t('backup.reconcileResult', { newCount: c.new, updated: c.updated, total: c.total }));
      qc.invalidateQueries({ queryKey: ['backups'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const deleteMut = useMutation({
    mutationFn: async (names: string[]) => {
      for (const name of names) {
        await mcpCall('toolbox.backup_delete', { backup: name });
      }
      return { deleted: names.length };
    },
    onSuccess: (r) => {
      toast.success(t('backup.deletedCount', { count: r.deleted }));
      setSelectedBackups(new Set());
      qc.invalidateQueries({ queryKey: ['backups'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  // Ochrana konkrétní zálohy před automatickým úklidem. Retence se jinak
  // nastavuje per plán — tohle je jednorázová pojistka nad jednou zálohou.
  const protectMut = useMutation({
    mutationFn: (opts: { names: string[]; protect: boolean }) =>
      mcpCall<{ changed?: number }>('toolbox.backup_protect', {
        names: opts.names,
        protected: opts.protect,
      }),
    onSuccess: (r, opts) => {
      toast.success(
        opts.protect
          ? t('backup.protectedCount', { count: r?.changed ?? opts.names.length })
          : t('backup.unprotectedCount', { count: r?.changed ?? opts.names.length }),
      );
      setSelectedBackups(new Set());
      qc.invalidateQueries({ queryKey: ['backups'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const restoreMut = useMutation({
    mutationFn: (name: string) =>
      mcpCall<{ ok?: boolean; pre_restore_backup?: string; error?: string }>(
        'toolbox.backup_restore',
        { backup: name },
      ),
    onSuccess: (r, name) => {
      if (r && 'error' in r && r.error) {
        toast.error(r.error);
        return;
      }
      const pre = r?.pre_restore_backup;
      toast.success(
        pre
          ? t('backup.restoreOkPre', { name, pre })
          : t('backup.restoreOk', { name }),
      );
      qc.invalidateQueries({ queryKey: ['backups'] });
    },
    onError: (e: Error) => toast.error(t('backup.restoreFailed', { msg: e.message })),
  });

  const backups: BackupMeta[] = Array.isArray(backupsData) ? (backupsData as BackupMeta[]) : [];

  const targetOptions = Array.from(
    new Set(backups.map((b) => backupTargetLabel(b)).filter((x) => x && x !== '—')),
  ).sort();

  const filtered = backups
    .filter((b) => {
      const q = search.trim().toLowerCase();
      if (q) {
        const hay = `${b.name} ${backupTargetLabel(b)} ${b.target_type ?? ''} ${b.image ?? ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (filterTarget !== 'all' && backupTargetLabel(b) !== filterTarget) return false;
      return true;
    })
    .sort((a, b) => {
      if (sortBy === 'name') return a.name.localeCompare(b.name);
      if (sortBy === 'size') return (b.size_bytes || 0) - (a.size_bytes || 0);
      const ta = new Date(backupCreatedAt(a) || 0).getTime();
      const tb = new Date(backupCreatedAt(b) || 0).getTime();
      return tb - ta;
    });

  const totalSize = backups.reduce((sum, b) => sum + (b.size_bytes || 0), 0);
  const moduleCount = new Set(
    backups.map((b) => backupTargetLabel(b)).filter((x) => x && x !== '—'),
  ).size;

  const toggleSelect = (id: string) => {
    setSelectedBackups((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAllFiltered = () => {
    if (filtered.length > 0 && filtered.every((b) => selectedBackups.has(b.id))) {
      setSelectedBackups(new Set());
    } else {
      setSelectedBackups(new Set(filtered.map((b) => b.id)));
    }
  };

  const confirmRestore = async (b: BackupMeta) => {
    const target = backupTargetLabel(b);
    if (
      await themedConfirm(
        t('backup.restoreConfirm', {
          name: b.name,
          target,
        }),
      )
    ) {
      restoreMut.mutate(b.name);
    }
  };

  const stats = (
    <StatsGrid columns={4} className='gap-2 sm:gap-4'>
      <StatCard label={t('backup.statBackups')} value={backups.length} icon={Archive} className='p-3 sm:p-5' />
      <StatCard label={t('backup.statTotalSize')} value={formatBytes(totalSize)} icon={HardDrive} className='p-3 sm:p-5' />
      <StatCard label={t('backup.statTargets')} value={moduleCount} icon={Box} className='p-3 sm:p-5' />
      <StatCard label={t('backup.statScheduled')} value={scheduleCount} icon={Clock} className='p-3 sm:p-5' />
    </StatsGrid>
  );

  const listCard = (
      <Card>
        <CardHeader className='flex-col sm:flex-row items-start sm:items-center gap-2'>
          <div className='min-w-0 flex-1'>
            <CardTitle icon={Archive}>
              {t('backup.backupListTitle', { count: filtered.length })}
              {filtered.length !== backups.length && (
                <span className='text-gray-600 font-normal text-xs ml-1'>
                  / {backups.length}
                </span>
              )}
            </CardTitle>
          </div>
          <div className='flex flex-wrap items-center gap-2 w-full sm:w-auto'>
            {selectedBackups.size > 0 && (
              <>
                <span className='text-xs text-gray-400'>
                  {t('backup.selectedCount', { count: selectedBackups.size })}
                </span>
                <Button
                  variant='secondary'
                  size='sm'
                  loading={protectMut.isPending}
                  disabled={protectMut.isPending}
                  onClick={() =>
                    protectMut.mutate({
                      names: backups.filter((b) => selectedBackups.has(b.id)).map((b) => b.name),
                      protect: true,
                    })}
                  icon={<Shield size={12} />}
                  title={t('backup.protectHint')}
                >
                  {t('backup.protect')}
                </Button>
                <Button
                  variant='ghost'
                  size='sm'
                  disabled={protectMut.isPending}
                  onClick={() =>
                    protectMut.mutate({
                      names: backups.filter((b) => selectedBackups.has(b.id)).map((b) => b.name),
                      protect: false,
                    })}
                >
                  {t('backup.unprotect')}
                </Button>
                <Button
                  variant='danger'
                  size='sm'
                  loading={deleteMut.isPending}
                  onClick={async () => {
                    const names = backups
                      .filter((b) => selectedBackups.has(b.id))
                      .map((b) => b.name);
                    const listPreview = names.slice(0, 5).join('\n') +
                      (names.length > 5 ? `\n${t('backup.andMore', { count: names.length - 5 })}` : '');
                    if (await themedConfirm(t('backup.deleteSelectedConfirm', { count: names.length, list: listPreview }))) {
                      deleteMut.mutate(names);
                    }
                  }}
                  icon={<Trash2 size={12} />}
                >
                  {t('backup.deleteSelected')}
                </Button>
              </>
            )}
            {!phone && (
              <Button
                variant='ghost'
                size='sm'
                onClick={() => setListView(listView === 'cards' ? 'list' : 'cards')}
                icon={listView === 'cards' ? <Table2 size={12} /> : <LayoutGrid size={12} />}
                title={listView === 'cards' ? t('backup.tableView') : t('backup.cardsView')}
              >
                {listView === 'cards' ? t('backup.tableView') : t('backup.cardsView')}
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className='space-y-3 min-w-0'>
          {/* Filters */}
          <div className='flex flex-col sm:flex-row gap-2'>
            <div className='relative flex-1 min-w-0'>
              <Search size={13} className='absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-600' />
              <input
                className='w-full bg-gray-950 border border-gray-800 rounded-xl pl-8 pr-3 py-2 text-sm text-gray-200 placeholder-gray-600 focus:border-blue-500 focus:outline-none'
                placeholder={t('backup.searchPlaceholder')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <Select
              size='sm'
              className='sm:w-44'
              value={filterTarget}
              onChange={(e) => setFilterTarget(e.target.value)}
              options={[
                { value: 'all', label: t('backup.filterAllTargets') },
                ...targetOptions.map((x) => ({ value: x, label: x })),
              ]}
            />
            <Select
              size='sm'
              className='sm:w-36'
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as 'date' | 'size' | 'name')}
              options={[
                { value: 'date', label: t('backup.sortDate') },
                { value: 'size', label: t('backup.sortSize') },
                { value: 'name', label: t('backup.sortName') },
              ]}
            />
          </div>

          {loadingBackups
            ? (
              <div className='flex justify-center py-10'>
                <RefreshCw size={16} className='animate-spin text-gray-600' />
              </div>
            )
            : backups.length === 0
            ? (
              <EmptyState
                icon={<Archive size={24} />}
                title={t('backup.noBackups')}
                description={t('backup.noBackupsDesc')}
              />
            )
            : filtered.length === 0
            ? (
              <EmptyState
                icon={<Filter size={24} />}
                title={t('backup.noFilterMatch')}
                description={t('backup.noFilterMatchDesc')}
              />
            )
            : effectiveListView === 'cards'
            ? (
              <>
                <div className='flex items-center gap-2 text-xs text-gray-500'>
                  <button
                    type='button'
                    onClick={selectAllFiltered}
                    className='inline-flex items-center gap-1.5 hover:text-gray-300'
                  >
                    <span className={`w-3.5 h-3.5 rounded border flex items-center justify-center ${
                      filtered.every((b) => selectedBackups.has(b.id)) && filtered.length > 0
                        ? 'border-blue-500 bg-blue-500'
                        : 'border-gray-600'
                    }`}>
                      {filtered.every((b) => selectedBackups.has(b.id)) && filtered.length > 0 && (
                        <Check size={9} className='text-white' />
                      )}
                    </span>
                    {t('backup.selectAllLabel')}
                  </button>
                </div>
                <div className='grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3'>
                  {filtered.map((b) => {
                    const age = backupAgeDays(b);
                    const created = backupCreatedAt(b);
                    const sizeStr = b.size_bytes ? formatBytes(b.size_bytes) : (b.size ?? '—');
                    const ok = !b.status || b.status === 'ok' || b.status === 'completed';
                    return (
                      <div
                        key={b.id}
                        className={`rounded-2xl border p-3.5 flex flex-col gap-2.5 transition-colors ${
                          selectedBackups.has(b.id)
                            ? 'border-blue-500/40 bg-blue-500/5'
                            : 'border-gray-800 bg-gray-950/50 hover:border-gray-700'
                        }`}
                      >
                        <div className='flex items-start gap-2.5 min-w-0'>
                          <button
                            type='button'
                            onClick={() => toggleSelect(b.id)}
                            className={`mt-0.5 w-4 h-4 rounded border shrink-0 flex items-center justify-center ${
                              selectedBackups.has(b.id)
                                ? 'border-blue-500 bg-blue-500'
                                : 'border-gray-600 hover:border-gray-500'
                            }`}
                          >
                            {selectedBackups.has(b.id) && <Check size={10} className='text-white' />}
                          </button>
                          <div className={`rounded-xl p-2 shrink-0 ${ok ? 'bg-emerald-500/10 text-emerald-400' : 'bg-red-500/10 text-red-400'}`}>
                            <Archive size={15} />
                          </div>
                          <div className='min-w-0 flex-1'>
                            <div className='font-mono text-xs text-white break-all leading-snug' title={b.name}>
                              {b.name}
                            </div>
                            <div className='flex flex-wrap items-center gap-1 mt-1'>
                              <Badge variant='gray' className='text-[10px] font-mono max-w-full truncate'>
                                {backupTargetLabel(b)}
                              </Badge>
                              {b.target_type && (
                                <Badge variant='info' className='text-[10px]'>{b.target_type}</Badge>
                              )}
                              <Badge variant={ok ? 'success' : 'error'} className='text-[10px]'>
                                {b.status || 'ok'}
                              </Badge>
                            </div>
                          </div>
                        </div>

                        <div className='grid grid-cols-2 gap-1 text-[11px] px-0.5'>
                          <span className='text-gray-600'>{t('backup.colSize')}</span>
                          <span className='text-right font-mono text-gray-300'>{sizeStr}</span>
                          <span className='text-gray-600'>{t('backup.colCreated')}</span>
                          <span className={`text-right font-mono ${ageTone(age)}`} title={created || undefined}>
                            {age !== null
                              ? t('backup.ageDays', { days: age })
                              : t('backup.noDate')}
                          </span>
                        </div>

                        <div className='flex flex-wrap gap-1.5 pt-2 border-t border-gray-800/80 mt-auto'>
                          <Button
                            variant='secondary'
                            size='sm'
                            loading={restoreMut.isPending && restoreMut.variables === b.name}
                            onClick={() => confirmRestore(b)}
                            icon={<Download size={12} />}
                          >
                            {t('backup.restore')}
                          </Button>
                          <Button
                            variant='ghost'
                            size='sm'
                            className='ml-auto text-red-400/80'
                            onClick={async () => {
                              if (await themedConfirm(t('backup.deleteBackupConfirm', { name: b.name }))) {
                                deleteMut.mutate([b.name]);
                              }
                            }}
                            icon={<Trash2 size={12} />}
                          >
                            {t('backup.delete')}
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )
            : (
              <div className='space-y-1'>
                <div className='flex items-center gap-2 pb-2 border-b border-gray-800 text-xs text-gray-500'>
                  <button
                    type='button'
                    onClick={selectAllFiltered}
                    className={`w-4 h-4 rounded border flex items-center justify-center ${
                      filtered.every((b) => selectedBackups.has(b.id)) && filtered.length > 0
                        ? 'border-blue-500 bg-blue-500'
                        : 'border-gray-600'
                    }`}
                  >
                    {filtered.every((b) => selectedBackups.has(b.id)) && filtered.length > 0 && (
                      <Check size={10} className='text-white' />
                    )}
                  </button>
                  <span>{t('backup.selectAllLabel')}</span>
                </div>
                {/* Desktop table */}
                <div className='hidden md:block overflow-x-auto'>
                  <table className='w-full text-sm'>
                    <thead>
                      <tr className='text-[11px] text-gray-500 border-b border-gray-800'>
                        <th className='w-8 py-2' />
                        <th className='text-left py-2 pr-2'>{t('backup.colName')}</th>
                        <th className='text-left py-2 pr-2'>{t('backup.colTarget')}</th>
                        <th className='text-right py-2 pr-2'>{t('backup.colSize')}</th>
                        <th className='text-right py-2 pr-2'>{t('backup.colCreated')}</th>
                        <th className='text-left py-2 pr-2'>{t('backup.colStatus')}</th>
                        <th className='text-right py-2'>{t('backup.colActions')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.map((b) => {
                        const age = backupAgeDays(b);
                        const created = backupCreatedAt(b);
                        const sizeStr = b.size_bytes ? formatBytes(b.size_bytes) : (b.size ?? '—');
                        const ok = !b.status || b.status === 'ok' || b.status === 'completed';
                        return (
                          <tr key={b.id} className='border-b border-gray-800/40 hover:bg-gray-900/30'>
                            <td className='py-2 pr-1'>
                              <button
                                type='button'
                                onClick={() => toggleSelect(b.id)}
                                className={`w-4 h-4 rounded border flex items-center justify-center ${
                                  selectedBackups.has(b.id)
                                    ? 'border-blue-500 bg-blue-500'
                                    : 'border-gray-600'
                                }`}
                              >
                                {selectedBackups.has(b.id) && <Check size={10} className='text-white' />}
                              </button>
                            </td>
                            <td className='py-2 pr-2 font-mono text-xs text-white max-w-[14rem] truncate' title={b.name}>
                              {b.name}
                            </td>
                            <td className='py-2 pr-2 text-xs text-gray-400 font-mono truncate max-w-[10rem]'>
                              {backupTargetLabel(b)}
                            </td>
                            <td className='py-2 pr-2 text-right font-mono text-xs text-gray-400'>{sizeStr}</td>
                            <td className={`py-2 pr-2 text-right font-mono text-xs ${ageTone(age)}`} title={created || undefined}>
                              {age !== null ? t('backup.ageDays', { days: age }) : '—'}
                            </td>
                            <td className='py-2 pr-2'>
                              <Badge variant={ok ? 'success' : 'error'} className='text-[10px]'>
                                {b.status || 'ok'}
                              </Badge>
                            </td>
                            <td className='py-2'>
                              <div className='flex justify-end gap-1'>
                                <Button
                                  variant='ghost'
                                  size='sm'
                                  onClick={() => confirmRestore(b)}
                                  icon={<Download size={12} />}
                                  title={t('backup.restore')}
                                />
                                <Button
                                  variant='ghost'
                                  size='sm'
                                  onClick={async () => {
                                    if (await themedConfirm(t('backup.deleteBackupConfirm', { name: b.name }))) {
                                      deleteMut.mutate([b.name]);
                                    }
                                  }}
                                  icon={<Trash2 size={12} />}
                                  title={t('backup.delete')}
                                />
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                {/* Mobile list rows */}
                <div className='md:hidden space-y-2'>
                  {filtered.map((b) => {
                    const age = backupAgeDays(b);
                    const sizeStr = b.size_bytes ? formatBytes(b.size_bytes) : (b.size ?? '—');
                    return (
                      <div key={b.id} className='rounded-xl border border-gray-800 bg-gray-950/40 p-2.5 space-y-2'>
                        <div className='flex items-start gap-2'>
                          <button
                            type='button'
                            onClick={() => toggleSelect(b.id)}
                            className={`mt-0.5 w-4 h-4 rounded border shrink-0 flex items-center justify-center ${
                              selectedBackups.has(b.id) ? 'border-blue-500 bg-blue-500' : 'border-gray-600'
                            }`}
                          >
                            {selectedBackups.has(b.id) && <Check size={10} className='text-white' />}
                          </button>
                          <div className='min-w-0 flex-1'>
                            <div className='font-mono text-xs text-white break-all'>{b.name}</div>
                            <div className='text-[11px] text-gray-500 mt-0.5'>{backupTargetLabel(b)}</div>
                          </div>
                        </div>
                        <div className='flex items-center justify-between text-[11px] pl-6'>
                          <span className='font-mono text-gray-400'>{sizeStr}</span>
                          <span className={`font-mono ${ageTone(age)}`}>
                            {age !== null ? t('backup.ageDays', { days: age }) : '—'}
                          </span>
                        </div>
                        <div className='flex gap-1.5 pl-6'>
                          <Button variant='secondary' size='sm' onClick={() => confirmRestore(b)} icon={<Download size={12} />}>
                            {t('backup.restore')}
                          </Button>
                          <Button
                            variant='ghost'
                            size='sm'
                            className='text-red-400/80'
                            onClick={async () => {
                              if (await themedConfirm(t('backup.deleteBackupConfirm', { name: b.name }))) {
                                deleteMut.mutate([b.name]);
                              }
                            }}
                            icon={<Trash2 size={12} />}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
        </CardContent>
      </Card>
  );

  return (
    <div className='space-y-4 p-1 min-w-0'>
      <PageHeader
        title={t('backup.pageTitle')}
        description={phone ? undefined : t('backup.pageDescription')}
        icon={Archive}
        actions={
          <div className='flex flex-wrap items-center gap-2'>
            <Button
              variant='secondary'
              size='sm'
              loading={reconcileMut.isPending}
              onClick={() => reconcileMut.mutate()}
              icon={<Shield size={12} />}
              title={t('backup.reconcileTitle')}
            >
              {t('backup.reconcile')}
            </Button>
          </div>
        }
      />

      <ModuleScopeBar
        node={node}
        onNodeChange={(n) => {
          setNode(n);
          setBackupScope({ node: n, identity: null });
          void qc.invalidateQueries({ queryKey: ['backups'] });
          void qc.invalidateQueries({ queryKey: ['backup-schedules'] });
        }}
        onRefresh={async () => {
          await refetch();
          await qc.invalidateQueries({ queryKey: ['backup-schedules', scopeKey] });
        }}
        refreshing={isFetching}
        sticky
      />

      {phone
        ? (
          <Tabs defaultTab='list'>
            <TabList>
              <Tab id='list' icon={<Archive size={13} />}>
                {t('backup.tabList', 'Zálohy')}
              </Tab>
              <Tab id='manual' icon={<Zap size={13} />}>
                {t('backup.tabManual', 'Ruční')}
              </Tab>
              <Tab id='schedule' icon={<CalendarClock size={13} />}>
                {t('backup.tabSchedule', 'Plán')}
              </Tab>
              <Tab id='retention' icon={<Scale size={13} />}>
                {t('backup.tabRetention', 'Retence')}
              </Tab>
              <Tab id='replica' icon={<Link2 size={13} />}>
                {t('backup.tabReplica', 'Cíle ACM')}
              </Tab>
            </TabList>
            <TabPanel id='list'>
              <div className='space-y-3'>
                {stats}
                {listCard}
              </div>
            </TabPanel>
            <TabPanel id='manual'>
              <ManualBackupPanel />
            </TabPanel>
            <TabPanel id='schedule'>
              <BackupSchedulerManager />
            </TabPanel>
            <TabPanel id='retention'>
              <RetentionManager />
            </TabPanel>
            <TabPanel id='replica'>
              <ReplicaContractsPanel />
            </TabPanel>
          </Tabs>
        )
        : (
          <>
            {stats}
            <ReplicaContractsPanel />
            <ManualBackupPanel />
            <BackupSchedulerManager />
            <RetentionManager />
            {listCard}
          </>
        )}
    </div>
  );
}

function formatBytes(bytes: number): string {
  if (!bytes || bytes <= 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
}
